<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <!-- Header -->
        <div class="mb-4">
          <v-btn text @click="volver">
            <v-icon left>mdi-arrow-left</v-icon>
            Volver
          </v-btn>
        </div>

        <!-- Loading inicial -->
        <v-progress-linear v-if="cargandoPostulacion" indeterminate color="primary" class="mb-4" />

        <v-alert  type="info" colored-border border="left" elevation="2" class="mb-4">
          <div class="d-flex align-center">
            <div>
              <div class="text-h6 mb-1">Fecha de Recepción</div>
              <span class="text-caption">La ficha debe presentarse dentro del plazo fijado en el oficio que regula el proceso especial, conforme al artículo 88 de la Ley N°21.724</span>
            </div>
          </div>
        </v-alert>

        <!-- Información del Certificado -->
        <v-alert v-if="!cargandoPostulacion && certificadoInfo" type="info" colored-border border="left" elevation="2" class="mb-4">
          <div class="d-flex align-center">
            <v-icon left size="28" class="mr-3">mdi-information</v-icon>
            <div>
              <div class="text-h6 mb-1">Tipo de Certificado</div>
              <v-chip :color="getTipoLeyColor(certificadoInfo.tipo)" small class="mr-2">
                {{ getTipoLeyTexto(certificadoInfo.tipo) }}
              </v-chip>
              <span class="text-caption">{{ getTipoLeyDescripcion(certificadoInfo.tipo) }}</span>
            </div>
          </div>
        </v-alert>

        <!-- Validación de Edad -->
        <v-alert v-if="!cargandoPostulacion && errorEdad" type="error" colored-border border="left" elevation="2" class="mb-4">
          <div class="d-flex align-center">
            <v-icon left size="28" class="mr-3">mdi-alert-circle</v-icon>
            <div>
              <div class="font-weight-bold mb-1">Error de Validación de Edad</div>
              <div>{{ errorEdad }}</div>
            </div>
          </div>
        </v-alert>

        <!-- Datos del Registro Civil (solo lectura) -->
        <v-card v-if="!cargandoPostulacion" class="mb-4">
          <v-card-title>
            <v-icon left class="mr-2">mdi-account</v-icon>
            Datos del Funcionario
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="6">
                <v-list dense>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-subtitle>RUT</v-list-item-subtitle>
                      <v-list-item-title class="text-h6">{{ postulacionData.rut }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-subtitle>Nombre Completo</v-list-item-subtitle>
                      <v-list-item-title>{{ postulacionData.nombre_completo }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-subtitle>Fecha de Nacimiento</v-list-item-subtitle>
                      <v-list-item-title>{{ formatearFecha(postulacionData.fecha_nacimiento) }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-col>
              <v-col cols="12" md="6">
                <v-list dense>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-subtitle>Sexo</v-list-item-subtitle>
                      <v-list-item-title>{{ postulacionData.sexo === 'M' ? 'Masculino' : 'Femenino' }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-subtitle>Estado Vital</v-list-item-subtitle>
                      <v-list-item-title>
                        <v-chip
                          :color="postulacionData.fallecido ? 'error' : 'success'"
                          small
                          dark
                        >
                          {{ postulacionData.fallecido ? 'Fallecido' : 'Vivo' }}
                        </v-chip>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-subtitle>Edad</v-list-item-subtitle>
                      <v-list-item-title>
                        {{ calcularEdad(postulacionData.fecha_nacimiento, postulacionData.fecha_fallecimiento) }} años
                        <v-chip
                          v-if="postulacionData.es_mayor_75"
                          x-small
                          color="info"
                          class="ml-1"
                        >
                          Mayor de 75
                        </v-chip>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Formulario de postulación -->
        <v-card v-if="!cargandoPostulacion">
          <v-card-title>
            <v-icon left class="mr-2">mdi-file-document-edit</v-icon>
            Información de la Postulación
          </v-card-title>
          <v-card-text>
            <v-form ref="form">
              <!-- Sector y Datos de Contacto -->
              <v-row>
                <v-col cols="12">
                  <h3 class="mb-3">Datos Generales</h3>
                </v-col>
                <v-col cols="12" md="6">
                  <label class="v-label theme--light mb-2">Sector *</label>
                  <v-radio-group
                    v-model="postulacion.sector"
                    row
                    :rules="[]"
                    class="mt-0"
                  >
                    <v-radio label="Municipal" value="municipal" />
                    <v-radio label="Código del trabajo" value="cementerio" />
                  </v-radio-group>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="postulacion.correo_electronico"
                    label="Correo Electrónico Personal *"
                    outlined
                    type="email"
                    :rules="[
                      // v => !!v || 'El correo electrónico es requerido',
                      v => /.+@.+\..+/.test(v) || 'Debe ser un correo válido'
                    ]"
                    prepend-inner-icon="mdi-email"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="postulacion.direccion"
                    label="Dirección *"
                    outlined
                    :rules="[]"
                    prepend-inner-icon="mdi-home"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="postulacion.numero_direccion"
                    label="Número"
                    outlined
                    prepend-inner-icon="mdi-numeric"
                  />
                </v-col>

                <!-- Atencion: Aqui en items estan las municipalidades, pero debiesen ser las comunas, cambiar despues -->
                <v-col cols="12" md="6">
                  <v-autocomplete
                    v-model="postulacion.comuna"
                    :items="payloadItemsMunicipalidades"
                    label="Comuna"
                    outlined
                    prepend-inner-icon="mdi-map-marker"
                    item-text="text"
                    item-value="text"
                    clearable
                  />
                </v-col>
              </v-row>

              <v-divider class="my-4" />

              <!-- Datos Laborales -->
              <v-row>
                <v-col cols="12">
                  <h3 class="mb-3">Información Laboral</h3>
                </v-col>
                <!-- Campos solo para Municipal -->
                <v-col v-if="postulacion.sector === 'municipal'" cols="12" md="4">
                  <v-select
                    v-model="postulacion.calidad_juridica"
                    :items="calidadesJuridicas"
                    label="Calidad Jurídica *"
                    outlined
                    :rules="[]"
                    prepend-inner-icon="mdi-briefcase"
                    @change="setCalidadJuridicaHistorial(0, postulacion.calidad_juridica)"
                  />
                </v-col>
                <v-col v-if="postulacion.sector === 'municipal'" cols="12" md="4">
                  <v-select
                    v-model="postulacion.estamento"
                    :items="estamentos"
                    label="Estamento *"
                    outlined
                    :rules="[]"
                    prepend-inner-icon="mdi-account-tie"
                  />
                </v-col>
                <v-col v-if="postulacion.sector === 'municipal'" cols="12" md="4">
                  <v-text-field
                    v-model.number="postulacion.grado"
                    label="Grado *"
                    outlined
                    type="number"
                    :rules="[
                      v => v !== null && v !== '' || 'El grado es requerido',
                      v => v >= 1 || 'El grado debe ser mayor a 0',
                      v => v < 20 || 'El grado no puede ser mayor a 19'
                    ]"
                    prepend-inner-icon="mdi-stairs"
                  />
                </v-col>
                <v-col v-if="postulacion.sector === 'cementerio'" cols="12" md="4">
                  <v-select
                    v-model="postulacion.codigo_trabajo_cementerio"
                    :items="codigosCementerio"
                    label="Codigo Trabajo *"
                    outlined
                    :rules="[]"
                    prepend-inner-icon="mdi-briefcase"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="remuneracionBrutaFormateada"
                    label="Remuneración Bruta *"
                    outlined
                    type="text"
                    prefix="$"
                    :rules="[v => v !== null && v !== '' || 'La remuneración es requerida']"
                    prepend-inner-icon="mdi-cash"
                    autocomplete="off"
                    inputmode="numeric"
                    hint="Promedio de remuneraciones de los últimos 12 meses previos a la postulación"
                    persistent-hint
                  >
                    <template #append>
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            v-on="on"
                            color="info"
                            small
                          >
                            mdi-information
                          </v-icon>
                        </template>
                        <span>Corresponde al promedio de las remuneraciones mensuales percibidas durante los últimos doce meses inmediatamente anteriores a la postulación</span>
                      </v-tooltip>
                    </template>
                  </v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="jornadaTrabajoFormateada"
                    label="Jornada de Trabajo (horas) *"
                    outlined
                    type="number"
                    suffix="hrs/semana"
                    :rules="[
                      v => v !== null && v !== '' || 'La jornada es requerida',
                      v => v <= this.jornadaMaxima || `La jornada no puede ser mayor a ${this.jornadaMaxima} horas semanales`,
                    ]"
                    prepend-inner-icon="mdi-clock-outline"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model.number="postulacion.dias_licencias_medicas"
                    label="N° Días de Licencias Médicas"
                    outlined
                    type="number"
                    suffix="días"
                    prepend-inner-icon="mdi-medical-bag"
                    :rules="[
                      v => v === null || v === '' || (v >= 0 && v <= 365) || 'Debe ser entre 0 y 365 días'
                    ]"
                    :hint="`Máximo 365 días del año ${new Date().getFullYear() - 1}`"
                    persistent-hint
                  >
                    <template #append>
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            v-on="on"
                            color="info"
                            small
                          >
                            mdi-information
                          </v-icon>
                        </template>
                        <span>Debe registrar el total de días de licencias médicas utilizadas dentro de los 365 días anteriores al inicio del período de postulación</span>
                      </v-tooltip>
                    </template>
                  </v-text-field>
                </v-col>
              </v-row>

              <v-divider class="my-4" />

              <!-- Beneficios previos - Solo para Municipal Y solo para Ley 21.135 (NO para Art. 88) -->
              <v-row v-if="postulacion.sector === 'municipal' && certificadoInfo && certificadoInfo.tipo === 'ley_21135'">
                <v-col cols="12">
                  <h3 class="mb-3">Beneficios Previos</h3>
                  <v-checkbox
                    v-model="postulacion.solicita_beneficios_previos"
                    label="Solicita beneficios previos al cumplimiento de las edades previstas en el Art. 1 Ley N° 21.135"
                    hide-details
                  />
                </v-col>
              </v-row>

              <v-divider class="my-4" />

              <!-- Causa del cese -->
              <v-row>
                <v-col cols="12">
                  <h3 class="mb-3">Causa del Cese</h3>
                  <v-radio-group
                    v-model="postulacion.causa_cese"
                    :rules="[]"
                  >
                    <v-radio
                      label="Renuncia voluntaria"
                      value="renuncia_voluntaria"
                    />
                    <!-- Opción solo para funcionarios municipales -->
                    <v-radio
                      v-if="postulacion.sector === 'municipal'"
                      label="Obtención de pensión de invalidez o declaración de vacancia por salud irrecuperable o incompatible"
                      value="pension_invalidez"
                    />
                    <!-- Opción solo para trabajadores de cementerio -->
                    <v-radio
                      v-if="postulacion.sector === 'cementerio'"
                      label="Art. 161, inciso primero del código del trabajo"
                      value="articulo_161"
                    />
                  </v-radio-group>
                </v-col>
              </v-row>

              <v-divider class="my-4" />

              <!-- Historial laboral municipal -->
              <div class="mb-4">
                <div class="d-flex align-center mb-3">
                  <h3>Historial Laboral en Municipalidades</h3>
                  <v-spacer />
                </div>

                <v-alert v-if="postulacion.historial_laboral.length === 0" type="info" text>
                  Debe agregar al menos un registro de trabajo en una municipalidad.
                </v-alert>

                <!-- Municipalidades que son iguales al mismo certificado -->
                <template v-for="(trabajo, index) in postulacion.historial_laboral">
                  <v-card
                    v-if="trabajo.municipalidad_id === certificadoMunicipalidadId"
                    :key="index"
                    outlined
                    class="mb-3"
                  >
                    <v-card-title class="py-2 d-flex align-center">
                      <v-icon left small>mdi-office-building</v-icon>
                      Municipalidad {{ index + 1 }} {{ aniosTrabajados(trabajo.trabajado) }}
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            v-on="on"
                            color="info"
                            small
                            class="ml-1"
                          >
                            mdi-information
                          </v-icon>
                        </template>
                        <span v-if="postulacion.sector === 'cementerio'">Registre los años de servicio acreditados en la administración municipal como trabajador de cementerio, continuos o discontinuos, conforme a la normativa aplicable</span>
                        <span v-else>Registre los años de servicio prestados en el municipio a la fecha de postulación, acreditados y regidos por Ley 18.883 o DL 3.551</span>
                      </v-tooltip>
                      <v-spacer />
                      <v-btn
                        icon
                        small
                        color="error"
                        v-if="index !== 0"
                        @click="eliminarMunicipalidad(index)"
                      >
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </v-card-title>
                    <v-card-text>
                      <v-row>
                        <v-col cols="12" md="3">
                          <v-autocomplete
                            v-model="trabajo.municipalidad_id"
                            :items="municipalidades"
                            item-text="nombre"
                            item-value="id"
                            label="Municipalidad *"
                            outlined
                            :rules="[v => !!v || 'La municipalidad es requerida']"
                            :loading="cargandoMunicipalidades"
                            prepend-inner-icon="mdi-city"
                            :disabled="index === 0"
                          />
                        </v-col>
                        <v-col cols="12" md="2">
                          <v-text-field
                            v-model="trabajo.fecha_desde"
                            label="Fecha Desde *"
                            type="date"
                            outlined
                            :rules="[v => !!v || 'La fecha es requerida']"
                            prepend-inner-icon="mdi-calendar-start"
                            @change="compruebaReglasHistorial(index)"
                          />
                        </v-col>
                        <v-col cols="12" md="2">
                          <v-text-field
                            v-model="trabajo.fecha_hasta"
                            label="Fecha Hasta *"
                            type="date"
                            outlined
                            :rules="[
                              v => !!v || 'La fecha es requerida',
                              v => !trabajo.fecha_desde || v >= trabajo.fecha_desde || 'La fecha hasta debe ser posterior a la fecha desde',
                              v => !v || new Date(v) <= new Date() || 'La fecha hasta no puede ser mayor al día actual'
                            ]"
                            prepend-inner-icon="mdi-calendar-end"
                            @change="compruebaReglasHistorial(index)"
                          />
                        </v-col>
                        <v-col cols="12" md="3">
                          <v-select
                            :disabled="index === 0 && postulacion.sector === 'municipal'"
                            v-model="trabajo.calidad_juridica"
                            :items="calidadesJuridicasHistorial"
                            label="Calidad Jurídica *"
                            outlined
                            :rules="[]"
                            prepend-inner-icon="mdi-briefcase"
                          />
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </template>


                <!-- Municipalidades restantes -->
                <h3 v-if="postulacion.historial_laboral.some(trabajo => trabajo.municipalidad_id !== certificadoMunicipalidadId)" class="mb-3">Historial Laboral en Otras Municipalidades</h3>
                <template v-for="(trabajo, index) in postulacion.historial_laboral">
                  <v-card
                    v-if="trabajo.municipalidad_id !== certificadoMunicipalidadId"
                    :key="index"
                    outlined
                    class="mb-3"
                  >
                    <v-card-title class="py-2 d-flex align-center">
                      <v-icon left small>mdi-office-building</v-icon>
                      Municipalidad {{ index + 1 }} {{ aniosTrabajados(trabajo.trabajado) }}
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            v-on="on"
                            color="info"
                            small
                            class="ml-1"
                          >
                            mdi-information
                          </v-icon>
                        </template>
                        <span v-if="postulacion.sector === 'cementerio'">Indique los años de servicio prestados en otros municipios y/o cementerios municipales, debidamente acreditados mediante certificado oficial válido</span>
                        <span v-else>Indique los años de servicio prestados en otros municipios, debidamente acreditados, bajo normas equivalentes a Ley 18.883 o DL 3.551</span>
                      </v-tooltip>
                      <v-spacer />
                      <v-btn
                        icon
                        small
                        color="error"
                        @click="eliminarMunicipalidad(index)"
                      >
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </v-card-title>
                    <v-card-text>
                      <v-row>
                        <v-col cols="12" md="3">
                          <v-autocomplete
                            v-model="trabajo.municipalidad_id"
                            :items="municipalidades"
                            item-text="nombre"
                            item-value="id"
                            label="Municipalidad *"
                            outlined
                            :rules="[v => !!v || 'La municipalidad es requerida']"
                            :loading="cargandoMunicipalidades"
                            prepend-inner-icon="mdi-city"
                          />
                        </v-col>
                        <v-col cols="12" md="2">
                          <v-text-field
                            v-model="trabajo.fecha_desde"
                            label="Fecha Desde *"
                            type="date"
                            outlined
                            :rules="[v => !!v || 'La fecha es requerida']"
                            prepend-inner-icon="mdi-calendar-start"
                            @change="compruebaReglasHistorial(index)"
                          />
                        </v-col>
                        <v-col cols="12" md="2">
                          <v-text-field
                            v-model="trabajo.fecha_hasta"
                            label="Fecha Hasta *"
                            type="date"
                            outlined
                            :rules="[
                              v => !!v || 'La fecha es requerida',
                              v => !trabajo.fecha_desde || v >= trabajo.fecha_desde || 'La fecha hasta debe ser posterior a la fecha desde',
                              v => !v || new Date(v) <= new Date() || 'La fecha hasta no puede ser mayor al día actual'
                            ]"
                            prepend-inner-icon="mdi-calendar-end"
                            @change="compruebaReglasHistorial(index)"
                          />
                        </v-col>
                        <v-col cols="12" md="2" v-if="trabajo.municipalidad_id !== certificadoMunicipalidadId">
                          <v-alert v-if="trabajo.certificado_archivo_url && !trabajo.certificado_archivo"
                            dense outlined type="info" class="mb-2">
                            <div class="d-flex align-center">
                              <v-icon left small>mdi-file-check</v-icon>
                              <span class="flex-grow-1 text-caption">Guardado</span>
                              <v-btn
                                x-small
                                text
                                color="primary"
                                @click="descargarArchivo(`certificado_antiguedad_${index}`)"
                              >
                                <v-icon small>mdi-download</v-icon>
                              </v-btn>
                            </div>
                          </v-alert>
                          <v-file-input
                            v-model="trabajo.certificado_archivo"
                            :label="trabajo.certificado_archivo_url ? 'Cambiar certificado de Antigüedad' : 'Certificado de Antigüedad *'"
                            outlined
                            :rules="[]"
                            accept="application/pdf,image/*"
                            prepend-icon=""
                            prepend-inner-icon="mdi-file-certificate"
                            show-size
                            :clearable="true"
                          >
                            <template #selection="{ text }">
                              <v-chip small label color="primary">
                                <v-icon left small>mdi-file-pdf-box</v-icon>
                                {{ text }}
                              </v-chip>
                            </template>
                          </v-file-input>
                        </v-col>
                        <v-col cols="12" md="3">
                          <v-select
                            v-model="trabajo.calidad_juridica"
                            :items="calidadesJuridicasHistorial"
                            label="Calidad Jurídica *"
                            outlined
                            :rules="[]"
                            prepend-inner-icon="mdi-briefcase"
                          />
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </template>

                <div class="d-flex align-center justify-end mb-3">
                  <v-btn color="primary" small @click="agregarMunicipalidad">
                    <v-icon left small>mdi-plus</v-icon>
                    Agregar Nuevo Periodo al historial laboral
                  </v-btn>
                </div>

                <v-card outlined class="mt-3">
                  <v-card-title>
                    <v-icon left small>mdi-calculator</v-icon>
                    Resumen de Tiempo Trabajado
                  </v-card-title>
                  <v-simple-table>
                    <template #default>
                      <thead>
                        <tr>
                          <th>Municipalidad</th>
                          <th class="text-center">Años</th>
                          <th class="text-center">Meses</th>
                          <th class="text-center">Días</th>
                          <th class="text-center">Aplica</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="periodo in totalTrabajado.periodos" :key="periodo.municipalidadId">
                          <td>{{ periodo.nombre }}</td>
                          <td class="text-center">{{ periodo.anios }}</td>
                          <td class="text-center">{{ periodo.meses }}</td>
                          <td class="text-center">{{ periodo.dias }}</td>
                          <td v-if="periodo.anios === 0 && periodo.meses === 0 && periodo.dias === 0" class="text-center">No</td>
                          <td v-else class="text-center">Si</td>
                        </tr>
                        <tr class="font-weight-bold primary--text">
                          <td>
                          TOTAL
                          <v-tooltip bottom>
                            <template #activator="{ on, attrs }">
                              <v-icon
                                v-bind="attrs"
                                v-on="on"
                                color="info"
                                small
                                class="ml-1"
                                style="margin-bottom: 3px;"
                              >
                                mdi-information
                              </v-icon>
                            </template>
                            <span v-if="postulacion.sector === 'cementerio'">Consigne el total de años efectivos de servicio acreditado desempeñados específicamente como trabajador del código del trabajo a la fecha de postulación</span>
                            <span v-else>Consigne el total de años efectivos de servicio municipal acreditado, continuos o discontinuos, excluyendo servicios fuera del régimen municipal</span>
                          </v-tooltip>

                          </td>
                          <td class="text-center">{{ totalTrabajado.total.anios }}</td>
                          <td class="text-center">{{ totalTrabajado.total.meses }}</td>
                          <td class="text-center">{{ totalTrabajado.total.dias }}</td>
                          <td v-if="totalTrabajado.total.anios === 0 && totalTrabajado.total.meses === 0 && totalTrabajado.total.dias === 0" class="text-center">No</td>
                          <td v-else class="text-center">Si</td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-card>
              </div>

              <v-divider class="my-4" />

              <!-- Beneficios solicitados -->
              <div class="mb-4">
                <h3 class="mb-3">Beneficios Solicitados</h3>
                <v-alert v-if="!postulacion.causa_cese" type="warning" text dense>
                  Primero debe seleccionar la causa del cese para ver los beneficios disponibles.
                </v-alert>
                
                <!-- CEMENTERIO: Solo Bonificación Adicional (siempre, da igual la causa) -->
                <v-row v-if="postulacion.sector === 'cementerio' && postulacion.causa_cese">
                  <!-- Checkbox Bonificación Adicional -->
                  <v-col cols="12">
                    <div class="d-flex align-end">
                      <v-checkbox
                        :disabled="totalTrabajadoHastaPeriodoPostulacion.anios < 10"
                        v-model="postulacion.solicita_bonificacion_adicional"
                        label="Solicita bonificación adicional"
                        hide-details
                      />
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            v-on="on"
                            color="info"
                            small
                            class="ml-1"
                            style="margin-bottom: 4px;"
                          >
                            mdi-information
                          </v-icon>
                        </template>
                        <span>Puede solicitar esta bonificación si acredita al menos 10 años de servicios continuos o discontinuos como trabajador del código del trabajo.</span>
                      </v-tooltip>
                    </div>
                    <div v-if="totalTrabajadoHastaPeriodoPostulacion.anios > 0" class="mt-2">
                      <span class="font-weight-bold">Tiempo efectivo: </span>
                      <span>
                        {{totalTrabajadoHastaPeriodoPostulacion.anios}} años - {{totalTrabajadoHastaPeriodoPostulacion.meses}} meses - {{totalTrabajadoHastaPeriodoPostulacion.dias}} dias
                      </span>
                      <span v-if="postulacion.solicita_bonificacion_adicional && postulacion.monto_uf_bonificacion_adicional >=10" class="font-weight-bold primary--text ml-5">
                        Monto: {{ postulacion.monto_uf_bonificacion_adicional }} UF
                      </span>
                      <v-tooltip bottom v-if="postulacion.solicita_bonificacion_adicional && postulacion.monto_uf_bonificacion_adicional >=10">
                        <template #activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            v-on="on"
                            color="info"
                            small
                            class="ml-1"
                            style="margin-bottom: 4px;"
                          >
                            mdi-information
                          </v-icon>
                        </template>
                        <span>El monto de la bonificación adicional se determina según tramos de años de servicio, con valores que van desde 400 a 560 UF</span>
                      </v-tooltip>
                    </div>
                  </v-col>

                  <!-- Campos adicionales para bonificación adicional -->
                  <!-- <v-col v-if="postulacion.solicita_bonificacion_adicional" cols="12" md="6">
                    <v-text-field
                      v-model.number="postulacion.anios_servicio_bonificacion_adicional"
                      label="Número de años de servicio *"
                      type="number"
                      outlined
                      :rules="[
                        v => postulacion.solicita_bonificacion_adicional ? (!!v || 'Los años de servicio son requeridos') : true,
                        v => !v || v >= 10 || 'Debe ser mayor a 9'
                      ]"
                      prepend-inner-icon="mdi-calendar-clock"
                      hint="Años de servicio para cálculo de bonificación"
                    />
                  </v-col> -->

                  <!-- <v-col v-if="postulacion.solicita_bonificacion_adicional" cols="12" md="6">
                    <v-text-field
                      v-model.number="postulacion.monto_uf_bonificacion_adicional"
                      label="Monto de la bonificación adicional en UF *"
                      type="number"
                      outlined
                      :rules="[
                        v => postulacion.solicita_bonificacion_adicional ? (!!v || 'El monto es requerido') : true,
                        v => !v || v > 0 || 'Debe ser mayor a 0'
                      ]"
                      prepend-inner-icon="mdi-currency-usd"
                      hint="Monto en UF de la bonificación adicional"
                      suffix="UF"
                    />
                  </v-col> -->
                </v-row>

                <!-- MUNICIPAL + RENUNCIA VOLUNTARIA: 4 beneficios -->
                <v-row v-if="postulacion.sector === 'municipal' && postulacion.causa_cese === 'renuncia_voluntaria'">
                  <!-- 1. Retiro Voluntario -->
                  <v-col cols="12">
                    <div class="d-flex align-end">
                      <v-checkbox
                        v-model="postulacion.solicita_bonificacion_retiro"
                        label="Solicita bonificación por retiro voluntario"
                        hide-details
                        :disabled="totalTrabajado.total.anios < 1"
                      />
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            v-on="on"
                            color="info"
                            small
                            class="ml-1"
                            style="margin-bottom: 4px;"
                          >
                            mdi-information
                          </v-icon>
                        </template>
                        <span>Se considerará un mes por año de servicio con tope de 6 meses</span>
                      </v-tooltip>
                    </div>
                    <div v-if="postulacion.solicita_bonificacion_retiro && totalTrabajado.total.anios > 0"
                        class="mt-2 font-weight-bold primary--text">
                      Monto retiro voluntario: {{ formatoPesos(postulacion.monto_retiro_voluntario) }}
                    </div>
                  </v-col>
                  <v-col cols="12">
                    <div class="d-flex align-end" v-if="postulacion.solicita_bonificacion_retiro">
                      <v-checkbox
                        v-if="postulacion.solicita_bonificacion_retiro"
                        v-model="postulacion.solicita_bonificacion_complementaria_retiro"
                        label="Solicita bonificación complementaria por retiro voluntario"
                        hide-details
                        :disabled="totalTrabajado.total.anios < 7"
                      />
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            v-on="on"
                            color="info"
                            small
                            class="ml-1"
                            style="margin-bottom: 4px;"
                          >
                            mdi-information
                          </v-icon>
                        </template>
                        <span>Se considerará un mes por año de servicio con tope de 5 meses</span>
                      </v-tooltip>
                    </div>
                    <div v-if="postulacion.solicita_bonificacion_retiro && totalTrabajado.total.anios > 0"
                        class="mt-2 font-weight-bold primary--text">
                      Monto retiro complementario: {{ formatoPesos(postulacion.monto_retiro_complementario) }}
                    </div>
                  </v-col>

                  <!-- 2. Bonificación Adicional -->
                  <v-col cols="12">
                    <div class="d-flex align-end">
                      <v-checkbox
                        :disabled="totalTrabajadoHastaPeriodoPostulacion.anios < 10"
                        v-model="postulacion.solicita_bonificacion_adicional"
                        label="Solicita bonificación adicional"
                        hide-details
                      />
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            v-on="on"
                            color="info"
                            small
                            class="ml-1"
                            style="margin-bottom: 4px;"
                          >
                            mdi-information
                          </v-icon>
                        </template>
                        <span>Puede solicitar la bonificación adicional si acredita al menos 10 años de servicios continuos o discontinuos al 02/01/{{new Date().getFullYear()}}.</span>
                      </v-tooltip>
                    </div>
                    <div v-if="totalTrabajadoHastaPeriodoPostulacion.anios > 0" class="mt-2">
                      <span class="font-weight-bold">Tiempo efectivo: </span>
                      <span>
                        {{totalTrabajadoHastaPeriodoPostulacion.anios}} años - {{totalTrabajadoHastaPeriodoPostulacion.meses}} meses - {{totalTrabajadoHastaPeriodoPostulacion.dias}} dias
                      </span>
                      <span v-if="postulacion.solicita_bonificacion_adicional && postulacion.monto_uf_bonificacion_adicional >=10" class="font-weight-bold primary--text ml-5">
                        Monto: {{ postulacion.monto_uf_bonificacion_adicional }} UF
                      </span>
                      <v-tooltip bottom v-if="postulacion.solicita_bonificacion_adicional && postulacion.monto_uf_bonificacion_adicional >=10">
                        <template #activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            v-on="on"
                            color="info"
                            small
                            class="ml-1"
                            style="margin-bottom: 4px;"
                          >
                            mdi-information
                          </v-icon>
                        </template>
                        <span>El monto de la bonificación adicional se determina según tramos de años de servicio, con valores que van desde 400 a 560 UF</span>
                      </v-tooltip>
                    </div>
                  </v-col>

                  <!-- Campos adicionales para bonificación adicional -->
                  <!-- <v-col v-if="postulacion.solicita_bonificacion_adicional" cols="12" md="6">
                    <v-text-field
                      v-model.number="postulacion.anios_servicio_bonificacion_adicional"
                      label="Número de años de servicio *"
                      type="number"
                      outlined
                      :rules="[
                        v => postulacion.solicita_bonificacion_adicional ? (!!v || 'Los años de servicio son requeridos') : true,
                        v => !v || v >= 10 || 'Debe ser mayor a 9'
                      ]"
                      prepend-inner-icon="mdi-calendar-clock"
                      hint="Años de servicio para cálculo de bonificación"
                      disabled
                    />
                    <div v-if="postulacion.solicita_bonificacion_adicional && postulacion.monto_uf_bonificacion_adicional >=10"
                        class="font-weight-bold primary--text text-right">
                      Monto: {{ postulacion.monto_uf_bonificacion_adicional }} UF
                    </div>
                  </v-col> -->
                  <!-- <v-col v-if="postulacion.solicita_bonificacion_adicional && postulacion.anios_servicio_bonificacion_adicional >=10" cols="12" md="6">
                    <v-text-field
                      v-model.number="postulacion.monto_uf_bonificacion_adicional"
                      label="Monto de la bonificación adicional en UF *"
                      type="number"
                      outlined
                      :rules="[
                        v => postulacion.solicita_bonificacion_adicional ? (!!v || 'El monto es requerido') : true,
                        v => !v || v > 0 || 'Debe ser mayor a 0'
                      ]"
                      prepend-inner-icon="mdi-currency-usd"
                      hint="Monto en UF de la bonificación adicional"
                      suffix="UF"
                      disabled
                    />
                  </v-col> -->

                  <!-- 3. Bono por Antigüedad -->
                  <v-col cols="12">
                    <div class="d-flex align-end">
                      <v-checkbox
                        :disabled="totalTrabajado.total.anios < 35"
                        v-model="postulacion.solicita_bono_antiguedad"
                        label="Solicita bono por antigüedad"
                        hide-details
                      />
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            v-on="on"
                            color="info"
                            small
                            class="ml-1"
                            style="margin-bottom: 4px;"
                          >
                            mdi-information
                          </v-icon>
                        </template>
                        <span>Puede solicitar este bono solo si acredita 35 o más años de servicios, continuos o discontinuos, en la administración municipal</span>
                      </v-tooltip>
                    </div>
                    <div v-if="postulacion.solicita_bono_antiguedad && postulacion.monto_uf_anios_antiguedad" class="mt-2 font-weight-bold primary--text">
                      <span>Monto por antigüedad: {{ postulacion.monto_uf_anios_antiguedad }} UF</span>
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            v-on="on"
                            color="info"
                            small
                            class="ml-1"
                            style="margin-bottom: 4px;"
                          >
                            mdi-information
                          </v-icon>
                        </template>
                        <span>El monto se calcula en UF según los años sobre 35, con tramos diferenciados y un tope máximo total de 100 UF</span>
                      </v-tooltip>
                    </div>
                  </v-col>

                  <!-- 4. Bono por Trabajo Pesado -->
                  <v-col cols="12">
                    <div class="d-flex align-end">
                      <v-checkbox
                        v-model="postulacion.tuvo_trabajo_pesado"
                        label="Solicita bono por trabajo pesado"
                        hide-details
                      />
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            v-on="on"
                            color="info"
                            small
                            class="ml-1"
                            style="margin-bottom: 4px;"
                          >
                            mdi-information
                          </v-icon>
                        </template>
                        <span>Marque esta opción si posee años cotizados o certificados como trabajo pesado, conforme a la normativa vigente</span>
                      </v-tooltip>
                    </div>
                  </v-col>

                  <!-- Campos adicionales para trabajo pesado -->
                  <v-col v-if="postulacion.tuvo_trabajo_pesado" cols="12" md="6">
                    <v-text-field
                      v-model.number="postulacion.anios_trabajo_pesado"
                      label="Años informados como trabajo pesado *"
                      type="number"
                      outlined
                      :rules="[
                        v => postulacion.tuvo_trabajo_pesado ? (!!v || 'Los años son requeridos') : true,
                        v => !v || v > 0 || 'Debe ser mayor a 0'
                      ]"
                      prepend-inner-icon="mdi-calendar-clock"
                      hint="Años de trabajo pesado informados"
                      @input="montoUfBonoTrabajo()"
                    />
                    <div v-if="postulacion.tuvo_trabajo_pesado && postulacion.monto_uf_trabajo_pesado"
                        class="font-weight-bold primary--text text-right">
                      <span>Monto: {{ Math.round(postulacion.monto_uf_trabajo_pesado) }} UF</span>
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            v-on="on"
                            color="info"
                            small
                            class="ml-1"
                            style="margin-bottom: 4px;"
                          >
                            mdi-information
                          </v-icon>
                        </template>
                        <span>El monto corresponde a 10 UF por cada año certificado como trabajo pesado, con un límite máximo de 100 UF</span>
                      </v-tooltip>
                    </div>
                  </v-col>

                  <!-- <v-col v-if="postulacion.tuvo_trabajo_pesado" cols="12" md="6">
                    <v-text-field
                      v-model.number="postulacion.monto_uf_trabajo_pesado"
                      label="Monto solicitado en UF por trabajo pesado *"
                      type="number"
                      outlined
                      :rules="[
                        v => postulacion.tuvo_trabajo_pesado ? (!!v || 'El monto es requerido') : true,
                        v => !v || v > 0 || 'Debe ser mayor a 0'
                      ]"
                      prepend-inner-icon="mdi-currency-usd"
                      hint="Monto en UF solicitado"
                      suffix="UF"
                      disabled
                    />
                  </v-col> -->
                </v-row>

                <!-- MUNICIPAL + PENSIÓN INVALIDEZ: Solo 2 beneficios -->
                <v-row v-if="postulacion.sector === 'municipal' && postulacion.causa_cese === 'pension_invalidez'">
                  <!-- 1. Retiro Voluntario -->
                  <!-- <v-col cols="12">
                    <div class="d-flex align-end">
                      <v-checkbox
                        v-model="postulacion.solicita_bonificacion_retiro"
                        label="Solicita bonificación por retiro voluntario"
                        hide-details
                        :disabled="totalTrabajado.total.anios < 1"
                      />
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            v-on="on"
                            color="info"
                            small
                            class="ml-1"
                            style="margin-bottom: 4px;"
                          >
                            mdi-information
                          </v-icon>
                        </template>
                        <span>Se considerará un mes por año de servicio con tope de 6 meses</span>
                      </v-tooltip>
                    </div>
                    <div v-if="postulacion.solicita_bonificacion_retiro && totalTrabajado.total.anios > 0"
                        class="mt-2 font-weight-bold primary--text">
                      Monto retiro voluntario: {{ formatoPesos(postulacion.monto_retiro_voluntario) }}
                    </div>
                  </v-col>
                  <v-col cols="12">
                    <div class="d-flex align-end" v-if="postulacion.solicita_bonificacion_retiro">
                      <v-checkbox
                        v-if="postulacion.solicita_bonificacion_retiro"
                        v-model="postulacion.solicita_bonificacion_complementaria_retiro"
                        label="Solicita bonificación complementaria por retiro voluntario"
                        hide-details
                        :disabled="totalTrabajado.total.anios < 7"
                      />
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            v-on="on"
                            color="info"
                            small
                            class="ml-1"
                            style="margin-bottom: 4px;"
                          >
                            mdi-information
                          </v-icon>
                        </template>
                        <span>Se considerará un mes por año de servicio con tope de 5 meses</span>
                      </v-tooltip>
                    </div>
                    <div v-if="postulacion.solicita_bonificacion_retiro && totalTrabajado.total.anios > 0"
                        class="mt-2 font-weight-bold primary--text">
                      Monto retiro complementario: {{ formatoPesos(postulacion.monto_retiro_complementario) }}
                    </div>
                  </v-col> -->

                  <!-- 2. Bonificación Adicional -->
                  <v-col cols="12">
                    <div class="d-flex align-end">
                      <v-checkbox
                        :disabled="totalTrabajadoHastaPeriodoPostulacion.anios < 30"
                        v-model="postulacion.solicita_bonificacion_adicional"
                        label="Solicita bonificación adicional"
                        hide-details
                      />
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            v-on="on"
                            color="info"
                            small
                            class="ml-1"
                            style="margin-bottom: 4px;"
                          >
                            mdi-information
                          </v-icon>
                        </template>
                        <span>Puede solicitar la bonificación adicional si acredita al menos 30 años de servicios continuos o discontinuos al 02/01/{{new Date().getFullYear()}}.</span>
                      </v-tooltip>
                    </div>
                    <div v-if="totalTrabajadoHastaPeriodoPostulacion.anios > 0" class="mt-2">
                      <span class="font-weight-bold">Tiempo efectivo: </span>
                      <span>
                        {{totalTrabajadoHastaPeriodoPostulacion.anios}} años - {{totalTrabajadoHastaPeriodoPostulacion.meses}} meses - {{totalTrabajadoHastaPeriodoPostulacion.dias}} dias
                      </span>
                      <span v-if="postulacion.solicita_bonificacion_adicional && postulacion.monto_uf_bonificacion_adicional >=10" class="font-weight-bold primary--text ml-5">
                        Monto: {{ postulacion.monto_uf_bonificacion_adicional }} UF
                      </span>
                      <v-tooltip bottom v-if="postulacion.solicita_bonificacion_adicional && postulacion.monto_uf_bonificacion_adicional >=10">
                        <template #activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            v-on="on"
                            color="info"
                            small
                            class="ml-1"
                            style="margin-bottom: 4px;"
                          >
                            mdi-information
                          </v-icon>
                        </template>
                        <span>El monto de la bonificación adicional se determina según tramos de años de servicio, con valores que van desde 400 a 560 UF</span>
                      </v-tooltip>
                    </div>
                  </v-col>

                  <!-- Campos adicionales para bonificación adicional -->
                  <!-- <v-col v-if="postulacion.solicita_bonificacion_adicional" cols="12" md="6">
                    <v-text-field
                      v-model.number="postulacion.anios_servicio_bonificacion_adicional"
                      label="Número de años de servicio *"
                      type="number"
                      outlined
                      :rules="[
                        v => postulacion.solicita_bonificacion_adicional ? (!!v || 'Los años de servicio son requeridos') : true,
                        v => !v || v >= 10 || 'Debe ser mayor a 9'
                      ]"
                      prepend-inner-icon="mdi-calendar-clock"
                      hint="Años de servicio para cálculo de bonificación"
                    />
                  </v-col> -->

                  <!-- <v-col v-if="postulacion.solicita_bonificacion_adicional" cols="12" md="6">
                    <v-text-field
                      v-model.number="postulacion.monto_uf_bonificacion_adicional"
                      label="Monto de la bonificación adicional en UF *"
                      type="number"
                      outlined
                      :rules="[
                        v => postulacion.solicita_bonificacion_adicional ? (!!v || 'El monto es requerido') : true,
                        v => !v || v > 0 || 'Debe ser mayor a 0'
                      ]"
                      prepend-inner-icon="mdi-currency-usd"
                      hint="Monto en UF de la bonificación adicional"
                      suffix="UF"
                    />
                  </v-col> -->
                </v-row>
              </div>

              <v-divider class="my-4" />

              <!-- Documentación adicional -->
              <div class="mb-4">
                <h3 class="mb-3">Documentación Requerida</h3>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-alert v-if="archivoPrepostulacionExistente && !postulacion.archivo_prepostulacion"
                      dense outlined type="info" class="mb-2">
                      <div class="d-flex align-center">
                        <v-icon left>mdi-file-check</v-icon>
                        <span class="flex-grow-1">Archivo guardado</span>
                        <v-btn
                          small
                          text
                          color="primary"
                          @click="descargarArchivo('prepostulacion')"
                        >
                          <v-icon left small>mdi-download</v-icon>
                          Ver/Descargar
                        </v-btn>
                      </div>
                    </v-alert>
                    <v-file-input
                      v-model="postulacion.archivo_prepostulacion"
                      :label="archivoPrepostulacionExistente ? 'Cambiar formulario entregado por el funcionario' : 'Formulario entregado por el funcionario *'"
                      outlined
                      :rules="[]"
                      accept="application/pdf,image/*"
                      prepend-icon=""
                      prepend-inner-icon="mdi-file-document"
                      show-size
                      :clearable="true"
                    >
                      <template #selection="{ text }">
                        <v-chip small label color="primary">
                          <v-icon left small>mdi-file-pdf-box</v-icon>
                          {{ text }}
                        </v-chip>
                      </template>
                    </v-file-input>
                  </v-col>
                </v-row>

                <!-- Certificado Trabajo Pesado - Solo si tiene trabajo pesado marcado -->
                <v-row v-if="postulacion.tuvo_trabajo_pesado && postulacion.causa_cese === 'renuncia_voluntaria'">
                  <v-col cols="12" md="6">
                    <v-alert v-if="archivoTrabajoPesadoExistente && !postulacion.archivo_trabajo_pesado"
                      dense outlined type="info" class="mb-2">
                      <div class="d-flex align-center">
                        <v-icon left>mdi-file-check</v-icon>
                        <span class="flex-grow-1">Archivo guardado</span>
                        <v-btn
                          small
                          text
                          color="primary"
                          @click="descargarArchivo('trabajo-pesado')"
                        >
                          <v-icon left small>mdi-download</v-icon>
                          Ver/Descargar
                        </v-btn>
                      </div>
                    </v-alert>
                    <v-file-input
                      v-model="postulacion.archivo_trabajo_pesado"
                      :label="archivoTrabajoPesadoExistente ? 'Cambiar certificado Trabajo Pesado' : 'Certificado Trabajo Pesado *'"
                      outlined
                      :rules="[]"
                      accept="application/pdf,image/*"
                      prepend-icon=""
                      prepend-inner-icon="mdi-hard-hat"
                      show-size
                      :clearable="true"
                    >
                      <template #selection="{ text }">
                        <v-chip small label color="primary">
                          <v-icon left small>mdi-file-pdf-box</v-icon>
                          {{ text }}
                        </v-chip>
                      </template>
                    </v-file-input>
                  </v-col>
                </v-row>

                <!-- Certificado de Invalidez - Solo si es pensión de invalidez -->
                <v-row v-if="postulacion.causa_cese === 'pension_invalidez'">
                  <v-col cols="12" md="6">
                    <!-- Mostrar archivo existente -->
                    <v-alert v-if="archivoInvalidezExistente && !postulacion.archivo_invalidez"
                      dense outlined type="info" class="mb-2">
                      <div class="d-flex align-center">
                        <v-icon left>mdi-file-check</v-icon>
                        <span class="flex-grow-1">Archivo guardado</span>
                        <v-btn
                          small
                          text
                          color="primary"
                          @click="descargarArchivo('invalidez')"
                        >
                          <v-icon left small>mdi-download</v-icon>
                          Ver/Descargar
                        </v-btn>
                      </div>
                    </v-alert>
                    <v-file-input
                      v-model="postulacion.archivo_invalidez"
                      :label="archivoInvalidezExistente ? 'Cambiar certificado de Invalidez' : 'Certificado de Invalidez *'"
                      outlined
                      :rules="[]"
                      accept="application/pdf,image/*"
                      prepend-icon=""
                      prepend-inner-icon="mdi-medical-bag"
                      show-size
                      :clearable="true"
                    >
                      <template #selection="{ text }">
                        <v-chip small label color="primary">
                          <v-icon left small>mdi-file-pdf-box</v-icon>
                          {{ text }}
                        </v-chip>
                      </template>
                    </v-file-input>
                  </v-col>
                </v-row>
              </div>

              <!-- Cumplimiento de Requisitos -->
              <v-divider class="my-6" />
              <v-row>
                <v-col cols="12">
                  <v-subheader class="px-0 text-h6">
                    <v-icon left color="primary">mdi-check-circle-outline</v-icon>
                    Cumplimiento de Requisitos
                  </v-subheader>
                </v-col>
              </v-row>
              
              <v-row>
                <v-col cols="12" md="6">
                  <v-alert
                    dense
                    text
                    type="info"
                    border="left"
                  >
                    <div class="text-subtitle-2 mb-2">
                      <v-icon small left>mdi-information</v-icon>
                      Información Importante
                    </div>
                    <div class="text-body-2">
                      Este campo indica si el funcionario cumple con todos los requisitos legales para postular al incentivo de retiro.
                      Puede marcar "No cumple" si detecta algún problema, pero igual puede enviar la postulación si el funcionario insiste.
                    </div>
                  </v-alert>
                </v-col>
                
                <v-col cols="12" md="6">
                  <v-radio-group
                    v-model="postulacion.cumple_requisitos"
                    :rules="[]"
                    :disabled="postulacion.causa_cese === 'pension_invalidez'"
                    row
                  >
                    <template #label>
                      <span class="text-subtitle-1 font-weight-medium">
                        ¿Cumple con los requisitos? *
                      </span>
                    </template>
                    <v-radio
                      :value="true"
                      label="Sí, cumple requisitos"
                      color="success"
                    >
                      <template #label>
                        <span class="success--text font-weight-medium">
                          <v-icon small color="success" left>mdi-check-circle</v-icon>
                          Sí, cumple requisitos
                        </span>
                      </template>
                    </v-radio>
                    <v-radio
                      :value="false"
                      label="No cumple requisitos"
                      color="error"
                    >
                      <template #label>
                        <span class="error--text font-weight-medium">
                          <v-icon small color="error" left>mdi-close-circle</v-icon>
                          No cumple requisitos
                        </span>
                      </template>
                    </v-radio>
                  </v-radio-group>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn text @click="volver">Cancelar</v-btn>
            <v-btn
              color="primary"
              :loading="guardando"
              @click="guardarPostulacion"
              :disabled="estadoPostulacion !== 'borrador'"
            >
              <v-icon left>mdi-content-save</v-icon>
              Guardar Postulación
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
// import { format } from 'date-fns'
// import es from 'date-fns/locale/es'
import { apiClient } from '@/admin/services/API'
import { notificar, notificarErrorResponse } from '@/admin/utils/notificar'

export default {
  name: 'IncentivosRetiroPostulacionForm',
  data() {
    return {
      tablaBonoAdicional: [
        {anio_min: 10, anio_max: 19, monto_uf: 400},
        {anio_min: 20, anio_max: 24, monto_uf: 440},
        {anio_min: 25, anio_max: 29, monto_uf: 480},
        {anio_min: 30, anio_max: 34, monto_uf: 520},
        {anio_min: 35, anio_max: 1000, monto_uf: 560},
      ],
      tablaBonoAntiguedad: [
        {anio: 35, monto_uf: 5},
        {anio: 36, monto_uf: 10},
        {anio: 37, monto_uf: 15},
        {anio: 38, monto_uf: 20},
        {anio: 39, monto_uf: 30},
        {anio: 40, monto_uf: 40},
        {anio: 41, monto_uf: 50},
        {anio: 42, monto_uf: 60},
        {anio: 43, monto_uf: 70},
        {anio: 44, monto_uf: 80},
        {anio: 45, monto_uf: 90},
        {anio: 46, monto_uf: 100},
      ],
      tablaBonoPesado: [
        {anio: 1, monto_uf: 10},
        {anio: 2, monto_uf: 20},
        {anio: 3, monto_uf: 30},
        {anio: 4, monto_uf: 40},
        {anio: 5, monto_uf: 50},
        {anio: 6, monto_uf: 60},
        {anio: 7, monto_uf: 70},
        {anio: 8, monto_uf: 80},
        {anio: 9, monto_uf: 90},
        {anio: 10, monto_uf: 100},
      ],
      cargandoPostulacion: false,
      guardando: false,
      cargandoMunicipalidades: false,
      certificadoInfo: null,
      errorEdad: null,
      certificadoMunicipalidadId: null,
      // URLs de archivos existentes
      archivoPrepostulacionExistente: null,
      archivoTrabajoPesadoExistente: null,
      archivoInvalidezExistente: null,
      postulacionData: {
        rut: '',
        nombre_completo: '',
        fecha_nacimiento: '',
        fecha_fallecimiento: '',
        sexo: '',
        fallecido: false,
        es_mayor_75: false,
      },
      postulacion: {
        // Datos generales
        sector: null,
        correo_electronico: '',
        direccion: '',
        numero_direccion: '',
        comuna: '',
        // Datos laborales
        codigo_trabajo_cementerio: null,
        calidad_juridica: null,
        estamento: null,
        grado: null,
        remuneracion_bruta: null,
        jornada_trabajo: null,
        dias_licencias_medicas: 0,
        // Causa del cese
        causa_cese: null,
        // Cumplimiento de requisitos
        cumple_requisitos: null,
        // Beneficios solicitados
        solicita_beneficios_previos: false,
        solicita_bonificacion_retiro: false,
        solicita_bonificacion_complementaria_retiro: false,
        solicita_bonificacion_adicional: false,
        anios_servicio_bonificacion_adicional: 0,
        monto_uf_bonificacion_adicional: null,
        solicita_bono_antiguedad: false,
        // Historial y archivos
        historial_laboral: [{
          municipalidad_id: null,
          municipalidad_nombre: '',
          fecha_desde: '',
          fecha_hasta: '',
          certificado_archivo: null,
          certificado_archivo_url: null,
          trabajado: {anios: 0, meses: 0, dias: 0, total_dias: 0},
          calidad_juridica: null,
        }],
        archivo_prepostulacion: null,
        tuvo_trabajo_pesado: false,
        archivo_trabajo_pesado: null,
        anios_trabajo_pesado: null,
        monto_uf_trabajo_pesado: null,
        anios_antiguedad: 0,
        monto_uf_anios_antiguedad: 0,
        monto_retiro_voluntario: 0,
        monto_retiro_complementario: 0,
        archivo_invalidez: null,
      },
      estadoPostulacion: null,
      municipalidades: [],
      calidadesJuridicas: [
        { text: 'Planta', value: 'planta' },
        { text: 'Contrata', value: 'contrata' },
      ],
      codigosCementerio: [
        { text: 'Vigilante municipal', value: 'vigilante municipal' },
        { text: 'Medico psicotecnico', value: 'medico psicotecnico' },
        { text: 'Cementerio', value: 'cementerio' },
      ],
      calidadesJuridicasHistorial: [
        { text: 'Planta', value: 'planta' },
        { text: 'Contrata', value: 'contrata' },
        { text: 'Codigo del trabajo', value: 'cementerio' },
      ],
      estamentos: [
        { text: 'Directivo', value: 'directivo' },
        { text: 'Jefatura', value: 'jefatura' },
        { text: 'Profesional', value: 'profesional' },
        { text: 'Técnico', value: 'tecnico' },
        { text: 'Administrativo', value: 'administrativo' },
        { text: 'Auxiliar', value: 'auxiliar' },
      ],
    }
  },
  watch: {
    'postulacion.sector'(newVal, oldVal) {
      // Limpiar causa_cese cuando cambia el sector porque las opciones son diferentes
      if (oldVal && newVal !== oldVal) {
        const causaActual = this.postulacion.causa_cese
        this.postulacion.historial_laboral[0].calidad_juridica = null

        // Si cambia a cementerio y tenía pension_invalidez, limpiar
        if (newVal === 'cementerio' && causaActual === 'pension_invalidez') {
          this.postulacion.causa_cese = null
        }
        
        // Si cambia a municipal y tenía articulo_161, limpiar
        if (newVal === 'municipal' && causaActual === 'articulo_161') {
          this.postulacion.causa_cese = null
        }
        
        // Si cambia a cementerio, limpiar campos exclusivos de municipal
        if (newVal === 'cementerio' && oldVal === 'municipal') {
          this.postulacion.calidad_juridica = null
          this.postulacion.estamento = null
          this.postulacion.grado = null

          this.postulacion.solicita_bonificacion_retiro = false
          this.postulacion.solicita_bonificacion_complementaria_retiro = false
          this.postulacion.solicita_bono_antiguedad = false
          this.postulacion.tuvo_trabajo_pesado = false
          this.postulacion.anios_trabajo_pesado = null
          this.postulacion.monto_uf_trabajo_pesado = null
          this.postulacion.archivo_trabajo_pesado = null
          this.postulacion.archivo_invalidez= null
        }

        this.montoUfBonoAdicional()
      }
    },
    'postulacion.causa_cese'(newVal, oldVal) {
      // Limpiar beneficios cuando cambia la causa de cese
      if (oldVal && newVal !== oldVal) {
        // Resetear beneficios según nueva causa
        if (newVal === 'renuncia_voluntaria') {
          // Limpiar beneficios de invalidez
          this.postulacion.solicita_bono_antiguedad = false
          this.postulacion.tuvo_trabajo_pesado = false
          this.postulacion.anios_trabajo_pesado = null
          this.postulacion.monto_uf_trabajo_pesado = null
          this.postulacion.archivo_trabajo_pesado = null
          this.postulacion.archivo_invalidez = null
        } else if (newVal === 'pension_invalidez') {
          // Limpiar beneficios de renuncia
          this.postulacion.solicita_bonificacion_retiro = false
          this.postulacion.monto_retiro_voluntario = 0

          this.postulacion.solicita_bonificacion_complementaria_retiro = false
          this.postulacion.monto_retiro_complementario = 0

          this.postulacion.solicita_bonificacion_adicional = false
          this.postulacion.anios_servicio_bonificacion_adicional = 0
          this.postulacion.monto_uf_bonificacion_adicional = null

          this.postulacion.solicita_bono_antiguedad = false
          this.postulacion.monto_uf_anios_antiguedad = null

          this.postulacion.tuvo_trabajo_pesado = false
          this.postulacion.anios_trabajo_pesado = null
          this.postulacion.monto_uf_trabajo_pesado = null
          this.postulacion.archivo_trabajo_pesado = null

          this.montoUfBonoAdicional()
        }
      }
    },
    'postulacion.tuvo_trabajo_pesado'(newVal) {
      // Limpiar campos de trabajo pesado si se desmarca el checkbox
      if (!newVal) {
        this.postulacion.anios_trabajo_pesado = null
        this.postulacion.monto_uf_trabajo_pesado = null
      }
    },
    'postulacion.solicita_bonificacion_adicional'(newVal) {
      // Limpiar campos de bonificación adicional si se desmarca el checkbox
      if (!newVal) {
        this.postulacion.anios_servicio_bonificacion_adicional = 0
        this.postulacion.monto_uf_bonificacion_adicional = null
      } else {
        this.montoUfBonoAdicional()
      }
    },
    'postulacion.jornada_trabajo'(newVal) {
      if (this.postulacion.sector === 'cementerio') {
        this.montoUfBonoAdicional()
      }
    },
    'postulacion.solicita_bono_antiguedad'(newVal) {
      this.montoUfBonoAntiguedad()
    },
    'postulacion.solicita_bonificacion_retiro'(newVal) {
      this.montoBonoRetiro()
    },
    'postulacion.solicita_bonificacion_complementaria_retiro'(newVal) {
      this.montoBonoRetiro()
    },
    'postulacion.historial_laboral': {
      handler() {
        this.montoBonoRetiro()
        this.montoUfBonoAdicional()
        this.montoUfBonoAntiguedad()
      },
      deep: true
    },
    'postulacion.remuneracion_bruta'(newVal) {
      this.montoBonoRetiro()
    }
  },
  mounted() {
    this.cargarDatos()
  },
  methods: {
    formatoPesos(valor) {
      if (valor == null || isNaN(valor)) return '$0';
      return valor.toLocaleString('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 });
    },
    async cargarDatos() {
      await this.cargarCertificado()
      await this.cargarPostulacion()
      if (!this.cargandoPostulacion) {
        await this.cargarMunicipalidades()
      }
      // Validar edad después de cargar todos los datos
      this.validarEdadSegunTipoCertificado()
    },
    porcentajeJornadaCementerio(horasSemanales) {
      const jornada = +horasSemanales
      let porcentaje = 100

      let jornadaCompleta = this.jornadaMaxima
      if (jornadaCompleta === 44) {
        jornadaCompleta = 42
      }

      if (jornada >= jornadaCompleta) return 100

      const porcentajeBruto = (jornada * 100) / jornadaCompleta
      porcentaje = Math.round(porcentajeBruto.toFixed(1))

      if (porcentaje <= 0) return 0

      return porcentaje
    },
    async cargarCertificado() {
      try {
        const certificadoId = this.$route.params.id
        const { data } = await apiClient.get(`/tramites/incentivos-retiro/certificados/${certificadoId}`)
        this.certificadoInfo = data.data
      } catch (error) {
        console.error('Error al cargar certificado:', error)
        notificarErrorResponse(error, 'Error al cargar información del certificado')
      }
    },
    async cargarPostulacion() {
      try {
        this.cargandoPostulacion = true
        const postulacionId = this.$route.params.postulacionId
        const { data } = await apiClient.get(`/tramites/incentivos-retiro/postulaciones/${postulacionId}`)
        
        // El backend retorna la postulación directamente, no envuelta en data.data
        const postulacion = data.data || data
        
        // Cargar datos básicos de la postulación
        this.postulacionData = {
          rut: postulacion.rut,
          nombre_completo: (postulacion.nombres || '') + ' ' + (postulacion.apellido_paterno || '') + ' ' + (postulacion.apellido_materno || ''),
          fecha_nacimiento: postulacion.fecha_nacimiento,
          fecha_fallecimiento: postulacion.fecha_fallecimiento,
          sexo: postulacion.sexo,
          fallecido: postulacion.fallecido,
          es_mayor_75: postulacion.es_mayor_75,
        }

        // Si la postulación ya tiene datos adicionales, cargarlos
        if (postulacion.sector) {
          this.postulacion.sector = postulacion.sector
        } else {
          this.postulacion.sector = 'municipal'
        }
        if (postulacion.correo_electronico) this.postulacion.correo_electronico = postulacion.correo_electronico
        if (postulacion.direccion) this.postulacion.direccion = postulacion.direccion
        if (postulacion.numero_direccion) this.postulacion.numero_direccion = postulacion.numero_direccion
        if (postulacion.comuna) this.postulacion.comuna = postulacion.comuna
        if (postulacion.calidad_juridica) this.postulacion.calidad_juridica = postulacion.calidad_juridica
        if (postulacion.codigo_trabajo_cementerio) this.postulacion.codigo_trabajo_cementerio = postulacion.codigo_trabajo_cementerio
        if (postulacion.estamento) this.postulacion.estamento = postulacion.estamento
        if (postulacion.grado !== undefined) this.postulacion.grado = postulacion.grado
        if (postulacion.remuneracion_bruta !== undefined) {
          let rb = postulacion.remuneracion_bruta
          if (typeof rb === 'string') rb = rb.replace(/\.00$/, '')
          this.postulacion.remuneracion_bruta = rb
        }
        if (postulacion.jornada_trabajo !== undefined) this.postulacion.jornada_trabajo = postulacion.jornada_trabajo
        if (postulacion.dias_licencias_medicas !== undefined) this.postulacion.dias_licencias_medicas = postulacion.dias_licencias_medicas
        if (postulacion.causa_cese) this.postulacion.causa_cese = postulacion.causa_cese
        if (postulacion.cumple_requisitos !== undefined && postulacion.cumple_requisitos !== null) this.postulacion.cumple_requisitos = postulacion.cumple_requisitos
        if (postulacion.solicita_beneficios_previos !== undefined) this.postulacion.solicita_beneficios_previos = postulacion.solicita_beneficios_previos
        if (postulacion.solicita_bonificacion_retiro !== undefined) this.postulacion.solicita_bonificacion_retiro = postulacion.solicita_bonificacion_retiro
        if (postulacion.solicita_bonificacion_complementaria_retiro !== undefined) this.postulacion.solicita_bonificacion_complementaria_retiro = postulacion.solicita_bonificacion_complementaria_retiro
        if (postulacion.solicita_bonificacion_adicional !== undefined) this.postulacion.solicita_bonificacion_adicional = postulacion.solicita_bonificacion_adicional
        if (postulacion.solicita_bono_antiguedad !== undefined) this.postulacion.solicita_bono_antiguedad = postulacion.solicita_bono_antiguedad
        if (postulacion.tuvo_trabajo_pesado !== undefined) this.postulacion.tuvo_trabajo_pesado = postulacion.tuvo_trabajo_pesado
        if (postulacion.anios_trabajo_pesado !== undefined) this.postulacion.anios_trabajo_pesado = postulacion.anios_trabajo_pesado
        if (postulacion.monto_uf_trabajo_pesado !== undefined) this.postulacion.monto_uf_trabajo_pesado = postulacion.monto_uf_trabajo_pesado
        if (postulacion.anios_servicio_bonificacion_adicional !== undefined) this.postulacion.anios_servicio_bonificacion_adicional = postulacion.anios_servicio_bonificacion_adicional
        if (postulacion.monto_uf_bonificacion_adicional !== undefined) this.postulacion.monto_uf_bonificacion_adicional = postulacion.monto_uf_bonificacion_adicional
        if (postulacion.monto_retiro_voluntario !== undefined) this.postulacion.monto_retiro_voluntario = postulacion.monto_retiro_voluntario
        if (postulacion.monto_retiro_complementario !== undefined) this.postulacion.monto_retiro_complementario = postulacion.monto_retiro_complementario
        if (postulacion.historial_laboral && Array.isArray(postulacion.historial_laboral)) {
          this.postulacion.historial_laboral = postulacion.historial_laboral
        } else {
          this.postulacion.historial_laboral[0].municipalidad_id = postulacion.certificado.municipalidad_id
        }
        this.certificadoMunicipalidadId = postulacion.certificado.municipalidad_id
        this.estadoPostulacion = postulacion.estado
        
        // Guardar URLs de archivos existentes
        if (postulacion.archivo_prepostulacion_url) {
          this.archivoPrepostulacionExistente = postulacion.archivo_prepostulacion_url
        }
        if (postulacion.archivo_trabajo_pesado_url) {
          this.archivoTrabajoPesadoExistente = postulacion.archivo_trabajo_pesado_url
        }
        if (postulacion.archivo_invalidez_url) {
          this.archivoInvalidezExistente = postulacion.archivo_invalidez_url
        }
      } catch (error) {
        notificarErrorResponse(error, 'Error al cargar la postulación')
        this.volver()
      } finally {
        this.cargandoPostulacion = false
      }
    },
    async cargarMunicipalidades() {
      try {
        this.cargandoMunicipalidades = true
        const { data } = await apiClient.get('usuarios/instituciones', { params: { tipo: 2 } })
        this.municipalidades = data.data || []
      } catch (error) {
        notificarErrorResponse(error, 'Error al cargar municipalidades')
      } finally {
        this.cargandoMunicipalidades = false
      }
    },
    formatearFecha(fecha) {
      if (!fecha) return '-'
      // Extrae solo la parte de la fecha (YYYY-MM-DD)
      const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(fecha)
      if (!match) return '-'
      const [, year, month, day] = match
      return `${day}/${month}/${year}`
      // if (!fecha) return '-'
      // return format(new Date(fecha), 'dd/MM/yyyy', { locale: es })
    },
    calcularEdad(fechaNacimiento, fechaFallecimiento = null) {
      if (!fechaNacimiento || fechaNacimiento === "0000-00-00") return 0
      const [year, month, day] = fechaNacimiento.split('T')[0].split('-').map(Number)
      const hoy = new Date()
      const nacimiento = new Date(year, month - 1, day)

      if (fechaFallecimiento && fechaFallecimiento !== "0000-00-00") {
        const [fYear, fMonth, fDay] = fechaFallecimiento.split('T')[0].split('-').map(Number)
        const fallecimiento = new Date(fYear, fMonth - 1, fDay)
        if (fallecimiento < hoy) {
          hoy.setTime(fallecimiento.getTime())
        }
      }

      let edad = hoy.getFullYear() - nacimiento.getFullYear()
      const mes = hoy.getMonth() - nacimiento.getMonth()
      if (mes < 0 || (mes === 0 && hoy.getDate() < nacimiento.getDate())) {
        edad--
      }
      return edad
    },
    agregarMunicipalidad() {
      const faltaCompletarFechas = this.postulacion.historial_laboral.some(t => (!t.fecha_desde || !t.fecha_hasta))
      if (faltaCompletarFechas) {
        notificar({ message: 'Debe completar las fechas de inicio y término de cada municipalidad antes de agregar otra', severity: 'error' })
        return
      }

      this.postulacion.historial_laboral.push({
        municipalidad_id: null,
        municipalidad_nombre: '',
        fecha_desde: '',
        fecha_hasta: '',
        certificado_archivo: null,
        certificado_archivo_url: null,
        trabajado: {anios: 0, meses: 0, dias: 0, total_dias: 0},
        calidad_juridica: null,
      })
    },
    eliminarMunicipalidad(index) {
      this.postulacion.historial_laboral.splice(index, 1)
    },
    encuentraNombreMunicipalidad(idMunicipalidad) {
      const municipalidad = this.payloadItemsMunicipalidades.find(m => m.value === +idMunicipalidad)
      return municipalidad !== undefined ? municipalidad.text : ''
    },
    validacionesAntesDeGuardar() {
      const faltaDireccion = !this.postulacion.direccion || this.postulacion.direccion.trim() === '' || !this.postulacion.numero_direccion || this.postulacion.numero_direccion.trim() === '' || !this.postulacion.comuna || this.postulacion.comuna.trim() === ''
      if (faltaDireccion) {
        notificar({ message: 'Debe ingresar la dirección, número o comuna', severity: 'error' })
        return true
      }

      const faltaCorreo = !this.postulacion.correo_electronico || this.postulacion.correo_electronico.trim() === ''
      if (faltaCorreo) {
        notificar({ message: 'Debe ingresar un correo electrónico de contacto', severity: 'error' })
        return true
      }

      if (this.postulacion.sector === 'municipal') {
         const faltaCalidadJuridicaGeneral = !this.postulacion.calidad_juridica
        if (faltaCalidadJuridicaGeneral) {
          notificar({ message: 'Debe seleccionar la calidad jurídica del funcionario', severity: 'error' })
          return true
        }

        const faltaEstamento = !this.postulacion.estamento
        if (faltaEstamento) {
          notificar({ message: 'Debe seleccionar el estamento del funcionario', severity: 'error' })
          return true
        }

        const faltaGrado = !this.postulacion.grado
        if (faltaGrado) {
          notificar({ message: 'Debe seleccionar el grado del funcionario', severity: 'error' })
          return true
        }
      }

      if (this.postulacion.sector === 'cementerio') {
        const faltaCodigoCementerio = !this.postulacion.codigo_trabajo_cementerio
        if (faltaCodigoCementerio) {
          notificar({ message: 'Debe seleccionar el código de trabajo', severity: 'error' })
          return true
        }
      }

      const faltaRemuneracion = this.postulacion.remuneracion_bruta === null || this.postulacion.remuneracion_bruta === undefined || this.postulacion.remuneracion_bruta <= 0
      if (faltaRemuneracion) {
        notificar({ message: 'Debe ingresar la remuneración bruta mensual del funcionario', severity: 'error' })
        return true
      }

      const faltaJornada = !this.postulacion.jornada_trabajo || this.postulacion.jornada_trabajo <= 0
      if (faltaJornada) {
        notificar({ message: 'Debe ingresar la jornada de trabajo semanal del funcionario', severity: 'error' })
        return true
      }

      const excedeJornadaMaxima = this.postulacion.jornada_trabajo > this.jornadaMaxima
      if (excedeJornadaMaxima) {
        notificar({ message: `La jornada de trabajo semanal no puede exceder ${this.jornadaMaxima} horas`, severity: 'error' })
        return true
      }

      const faltaDiasLicencias = this.postulacion.dias_licencias_medicas === null || this.postulacion.dias_licencias_medicas === undefined || this.postulacion.dias_licencias_medicas < 0
      if (faltaDiasLicencias) {
        notificar({ message: 'Debe ingresar los días de licencias médicas del funcionario', severity: 'error' })
        return true
      }

      const faltaCompletarMunicipalidad = this.postulacion.historial_laboral.some(t => !t.municipalidad_id)
      if (faltaCompletarMunicipalidad) {
        notificar({ message: 'Debe seleccionar una municipalidad para cada registro en el historial laboral', severity: 'error' })
        return true
      }

      const faltaCompletarFechas = this.postulacion.historial_laboral.some(t => (!t.fecha_desde || !t.fecha_hasta))
      if (faltaCompletarFechas) {
        notificar({ message: 'Debe completar las fechas de inicio y término para cada registro en el historial laboral', severity: 'error' })
        return true
      }

      const faltaCalidadJuridica = this.postulacion.historial_laboral.some(t => !t.calidad_juridica)
      if (faltaCalidadJuridica) {
        notificar({ message: 'Debe seleccionar la calidad jurídica para cada registro en el historial laboral', severity: 'error' })
        return true
      }

      const faltaArchivoCertificado = this.postulacion.historial_laboral.some(t => !t.certificado_archivo && !t.certificado_archivo_url && t.municipalidad_id !== this.certificadoMunicipalidadId)
      if (faltaArchivoCertificado) {
        notificar({ message: 'Debe adjuntar el certificado de antigüedad para cada registro en el historial laboral', severity: 'error' })
        return true
      }

      const faltaAniosTrabajoPesado = this.postulacion.tuvo_trabajo_pesado && (!this.postulacion.anios_trabajo_pesado || this.postulacion.anios_trabajo_pesado <= 0)
      if (faltaAniosTrabajoPesado) {
        notificar({ message: 'Debe ingresar los años de trabajo pesado', severity: 'error' })
        return true
      }

      const faltaArchivoTrabajoPesado = this.postulacion.tuvo_trabajo_pesado && !this.postulacion.archivo_trabajo_pesado && !this.archivoTrabajoPesadoExistente
      if (faltaArchivoTrabajoPesado) {
        notificar({ message: 'Debe adjuntar el certificado de trabajo pesado', severity: 'error' })
        return true
      }

      const faltaArchivoInvalidez = this.postulacion.causa_cese === 'pension_invalidez' && !this.postulacion.archivo_invalidez && !this.archivoInvalidezExistente
      if (faltaArchivoInvalidez) {
        notificar({ message: 'Debe adjuntar el certificado de invalidez', severity: 'error' })
        return true
      }

      const faltaArchivoPrepostulacion = !this.postulacion.archivo_prepostulacion && !this.archivoPrepostulacionExistente
      if (faltaArchivoPrepostulacion) {
        notificar({ message: 'Debe adjuntar el formulario entregado por el funcionario', severity: 'error' })
        return true
      }

      const faltaCumpleRequisitos = this.postulacion.cumple_requisitos === null || this.postulacion.cumple_requisitos === undefined
      if (faltaCumpleRequisitos) {
        notificar({ message: 'Debe indicar si el funcionario cumple con los requisitos para postular al incentivo de retiro', severity: 'error' })
        return true
      }

      return false
    },
    async guardarPostulacion() {
      if (this.estadoPostulacion !== 'borrador') {
        notificar({ message: 'Solo se pueden guardar cambios en postulaciones en estado borrador', severity: 'error' })
        return
      }
      // Nota: Permitimos guardar aunque no todos los campos estén completos
      // Las validaciones completas se harán al intentar generar el documento
      
      if (this.postulacion.historial_laboral.length === 0) {
        notificar({ message: 'Debe agregar al menos un registro de trabajo en una municipalidad', severity: 'warning' })
        // Permitimos continuar sin historial para guardar borrador
      }

      const hayErrores = this.validacionesAntesDeGuardar()
      if (hayErrores) return

      try {
        this.guardando = true

        // Preparar FormData para enviar archivos
        const formData = new FormData()
        if (this.postulacion.solicita_bonificacion_retiro) formData.append('monto_retiro_voluntario', this.postulacion.monto_retiro_voluntario)
        if (this.postulacion.solicita_bonificacion_complementaria_retiro) formData.append('monto_retiro_complementario', this.postulacion.monto_retiro_complementario)

        if (this.postulacion.monto_uf_anios_antiguedad > 0) formData.append('monto_uf_anios_antiguedad', this.postulacion.monto_uf_anios_antiguedad)
        if (this.postulacion.anios_antiguedad > 0) formData.append('anios_antiguedad', this.postulacion.anios_antiguedad)
        
        // Datos generales - solo si tienen valor
        if (this.postulacion.sector) formData.append('sector', this.postulacion.sector)
        if (this.postulacion.correo_electronico) formData.append('correo_electronico', this.postulacion.correo_electronico)
        if (this.postulacion.direccion) formData.append('direccion', this.postulacion.direccion)
        if (this.postulacion.numero_direccion) formData.append('numero_direccion', this.postulacion.numero_direccion)
        if (this.postulacion.comuna) formData.append('comuna', this.postulacion.comuna)
        
        // Datos laborales - solo si tienen valor
        if (this.postulacion.codigo_trabajo_cementerio) formData.append('codigo_trabajo_cementerio', this.postulacion.codigo_trabajo_cementerio)
        if (this.postulacion.calidad_juridica) formData.append('calidad_juridica', this.postulacion.calidad_juridica)
        if (this.postulacion.estamento) formData.append('estamento', this.postulacion.estamento)
        if (this.postulacion.grado !== null && this.postulacion.grado !== '') formData.append('grado', this.postulacion.grado)
        if (this.postulacion.remuneracion_bruta !== null && this.postulacion.remuneracion_bruta !== '') {
          let rb = this.postulacion.remuneracion_bruta;
          // Aqui si remuneracion bruta viene como 1000.00 se quitan los .00
          if (typeof rb === 'string') rb = rb.replace(/\.00$/, '');
          formData.append('remuneracion_bruta', rb)
        }
        if (this.postulacion.jornada_trabajo !== null && this.postulacion.jornada_trabajo !== '') formData.append('jornada_trabajo', this.postulacion.jornada_trabajo)
        if (this.postulacion.dias_licencias_medicas !== null && this.postulacion.dias_licencias_medicas !== '') formData.append('dias_licencias_medicas', this.postulacion.dias_licencias_medicas)
        
        // Causa del cese - solo si tiene valor
        if (this.postulacion.causa_cese) formData.append('causa_cese', this.postulacion.causa_cese)
        
        // Cumplimiento de requisitos - convertir a 1/0
        if (this.postulacion.cumple_requisitos !== null && this.postulacion.cumple_requisitos !== undefined) {
          formData.append('cumple_requisitos', this.postulacion.cumple_requisitos ? '1' : '0')
        }
        
        // Beneficios solicitados - convertir booleanos a 1/0
        // solicita_beneficios_previos: Solo para proceso Ley 21.135 (NO para Art. 88)
        if (this.certificadoInfo && this.certificadoInfo.tipo === 'ley_21135') {
          formData.append('solicita_beneficios_previos', this.postulacion.solicita_beneficios_previos ? '1' : '0')
        }
        formData.append('solicita_bonificacion_retiro', this.postulacion.solicita_bonificacion_retiro ? '1' : '0')
        formData.append('solicita_bonificacion_complementaria_retiro', this.postulacion.solicita_bonificacion_complementaria_retiro ? '1' : '0')
        formData.append('solicita_bonificacion_adicional', this.postulacion.solicita_bonificacion_adicional ? '1' : '0')
        formData.append('solicita_bono_antiguedad', this.postulacion.solicita_bono_antiguedad ? '1' : '0')
        
        // Trabajo pesado - convertir booleano a 1/0
        formData.append('tuvo_trabajo_pesado', this.postulacion.tuvo_trabajo_pesado ? '1' : '0')
        
        // Solo enviar montos/años si tienen valor
        if (this.postulacion.anios_trabajo_pesado !== null && this.postulacion.anios_trabajo_pesado !== '') {
          formData.append('anios_trabajo_pesado', this.postulacion.anios_trabajo_pesado)
        }
        if (this.postulacion.monto_uf_trabajo_pesado !== null && this.postulacion.monto_uf_trabajo_pesado !== '') {
          formData.append('monto_uf_trabajo_pesado', this.postulacion.monto_uf_trabajo_pesado)
        }
        if (this.postulacion.anios_servicio_bonificacion_adicional !== null && this.postulacion.anios_servicio_bonificacion_adicional !== '') {
          formData.append('anios_servicio_bonificacion_adicional', this.postulacion.anios_servicio_bonificacion_adicional)
        }
        if (this.postulacion.monto_uf_bonificacion_adicional !== null && this.postulacion.monto_uf_bonificacion_adicional !== '') {
          formData.append('monto_uf_bonificacion_adicional', this.postulacion.monto_uf_bonificacion_adicional)
        }
        
        // Historial laboral - solo si hay registros
        if (this.postulacion.historial_laboral.length > 0) {
          formData.append('historial_laboral', JSON.stringify(
            this.postulacion.historial_laboral.map(t => ({
              municipalidad_id: t.municipalidad_id,
              municipalidad_nombre: this.encuentraNombreMunicipalidad(t.municipalidad_id),
              fecha_desde: t.fecha_desde,
              fecha_hasta: t.fecha_hasta,
              trabajado: {anios: t.trabajado.anios, meses: t.trabajado.meses, dias: t.trabajado.dias, total_dias: t.trabajado.total_dias},
              calidad_juridica: t.calidad_juridica,
              certificado_archivo_url: t.certificado_archivo_url || null, // Preservar URL existente
            }))
          ))
          
          // Archivos del historial laboral
          this.postulacion.historial_laboral.forEach((trabajo, index) => {
            if (trabajo.certificado_archivo) {
              formData.append(`certificado_antiguedad_${index}`, trabajo.certificado_archivo)
            }
          })
        }
        
        // Archivos adicionales
        if (this.postulacion.archivo_prepostulacion) {
          formData.append('archivo_prepostulacion', this.postulacion.archivo_prepostulacion)
        }
        if (this.postulacion.archivo_trabajo_pesado) {
          formData.append('archivo_trabajo_pesado', this.postulacion.archivo_trabajo_pesado)
        }
        if (this.postulacion.archivo_invalidez) {
          formData.append('archivo_invalidez', this.postulacion.archivo_invalidez)
        }

        // Enviar al backend (actualizar postulación existente)
        const postulacionId = this.$route.params.postulacionId
        await apiClient.put(
          `/tramites/incentivos-retiro/postulaciones/${postulacionId}`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        )

        notificar({ message: 'Postulación guardada correctamente', severity: 'success' })
        this.volver()
      } catch (error) {
        notificarErrorResponse(error, 'Error al guardar la postulación')
      } finally {
        this.guardando = false
      }
    },
    volver() {
      this.$router.push({
        name: 'incentivos-retiro-certificado-detalle',
        params: { id: this.$route.params.id },
      })
    },
    getTipoLeyColor(tipo) {
      const colores = {
        ley_21135: 'info',
        ley_21724_menor_75: 'info',
        ley_21724_mayor_75: 'info',
      }
      return colores[tipo] || 'grey'
    },
    getTipoLeyTexto(tipo) {
      const textos = {
        ley_21135: 'Ley 21.135',
        ley_21724_menor_75: 'Ley 21.724 (Menor 75 años)',
        ley_21724_mayor_75: 'Ley 21.724 (Mayor 75 años)',
      }
      return textos[tipo] || tipo
    },
    getTipoLeyDescripcion(tipo) {
      const descripciones = {
        ley_21135: 'Proceso Regular - Hombres 65 años, Mujeres 60 años',
        ley_21724_menor_75: 'Artículo 88 - Postulantes de 66 años hasta antes de cumplir 75 años',
        ley_21724_mayor_75: 'Artículo 88 - Postulantes que cumplen 75 años o más (asignación inmediata)',
      }
      return descripciones[tipo] || ''
    },
    validarEdadSegunTipoCertificado() {
      if (!this.certificadoInfo || !this.postulacionData.fecha_nacimiento) {
        return
      }

      const fechaNacimiento = new Date(this.postulacionData.fecha_nacimiento)
      const hoy = new Date()
      const añoActual = hoy.getFullYear()
      const sexo = this.postulacionData.sexo
      const tipo = this.certificadoInfo.tipo
      
      // Calcular edad al 31 de diciembre del año actual
      const edad31Dic = añoActual - fechaNacimiento.getFullYear()
      
      // Calcular edad actual
      const edadActual = this.calcularEdad(this.postulacionData.fecha_nacimiento, this.postulacionData.fecha_fallecimiento)

      this.errorEdad = null

      if (tipo === 'ley_21135') {
        // Proceso Regular: Hombres >= 65, Mujeres >= 60
        if (sexo === 'M' && edad31Dic < 65) {
          this.errorEdad = `El funcionario no cumple con la edad mínima para Ley 21.135 (Proceso Regular). Hombres deben tener 65 años cumplidos al 31 de diciembre de ${añoActual}. Edad actual: ${edadActual} años.`
        } else if (sexo === 'F' && edad31Dic < 60) {
          this.errorEdad = `La funcionaria no cumple con la edad mínima para Ley 21.135 (Proceso Regular). Mujeres deben tener 60 años cumplidos al 31 de diciembre de ${añoActual}. Edad actual: ${edadActual} años.`
        }
      } else if (tipo === 'ley_21724_menor_75') {
        // Art. 88 Menor 75: Debe tener entre 66 y 74 años
        if (edadActual < 66) {
          this.errorEdad = `El postulante no cumple con la edad mínima para Ley 21.724 (Menor 75 años). Debe tener al menos 66 años cumplidos. Edad actual: ${edadActual} años.`
        }
        if (edadActual >= 75) {
          this.errorEdad = `El postulante tiene ${edadActual} años, por lo tanto debe postular bajo "Ley 21.724 (Mayor 75 años)" con asignación inmediata de cupo.`
        }
      } else if (tipo === 'ley_21724_mayor_75') {
        // Art. 88 Mayor 75: Debe tener 75 años o más
        if (edadActual < 75) {
          this.errorEdad = `El postulante no cumple con la edad para Ley 21.724 (Mayor 75 años). Debe tener 75 años cumplidos. Edad actual: ${edadActual} años.`
        }
      }
    },
    calcularAniosTrabajados(desde, hasta) {
      if (!desde || !hasta) {
        return {anios: 0, meses: 0, dias: 0, total_dias: 0}
      }

      const [yearDesde, monthDesde, dayDesde] = desde.split('-').map(Number)
      const [yearHasta, monthHasta, dayHasta] = hasta.split('-').map(Number)

      const fechaDesde = new Date(yearDesde, monthDesde - 1, dayDesde)
      const fechaHasta = new Date(yearHasta, monthHasta - 1, dayHasta)

      // Diferencia en milisegundos
      const diferenciaMilisegundos = fechaHasta - fechaDesde

      if (diferenciaMilisegundos < 0) {
        return {anios: 0, meses: 0, dias: 0, total_dias: 0}
      }

      // Convertir a días
      const totalDias = Math.floor(diferenciaMilisegundos / (1000 * 60 * 60 * 24))

      // Calcular años completos
      let anios = fechaHasta.getFullYear() - fechaDesde.getFullYear()
      let meses = fechaHasta.getMonth() - fechaDesde.getMonth()
      let dias = fechaHasta.getDate() - fechaDesde.getDate()
      // console.log('debug', anios, meses, dias)

      if (dias < 0) {
        meses--
        // Obtener el número de días del mes anterior
        const mesAnterior = new Date(fechaHasta.getFullYear(), fechaHasta.getMonth(), 0)
        dias += mesAnterior.getDate()
      }

      if (meses < 0) {
        anios--
        meses += 12
      }

      return {anios: anios, meses: meses, dias: dias, total_dias: totalDias}
    },
    compruebaReglasHistorial(index) {
      this.$nextTick(() => {
        const trabajo = this.postulacion.historial_laboral[index]

        let fechaDesde = null
        let fechaHasta = null
        if (trabajo.fecha_desde) {
          const [dyear, dmonth, dday] = trabajo.fecha_desde.split('T')[0].split('-').map(Number)
          fechaDesde = new Date(dyear, dmonth - 1, dday)
        }
        if (trabajo.fecha_hasta) {
          const [hyear, hmonth, hday] = trabajo.fecha_hasta.split('T')[0].split('-').map(Number)
          fechaHasta = new Date(hyear, hmonth - 1, hday)
        }

        if(trabajo.fecha_hasta && fechaHasta > new Date()) {
          this.$set(this.postulacion.historial_laboral[index], 'fecha_hasta', null)
          notificar({ message: 'La fecha de término no puede ser mayor a la fecha actual. Se ha limpiado el campo "Fecha Hasta".', severity: 'error' })
          return
        }

        if(trabajo.fecha_desde && fechaDesde > new Date()) {
          this.$set(this.postulacion.historial_laboral[index], 'fecha_desde', null)
          notificar({ message: 'La fecha de inicio no puede ser mayor a la fecha actual. Se ha limpiado el campo "Fecha Desde".', severity: 'error' })
          return
        }

        if (trabajo.fecha_desde && trabajo.fecha_hasta && fechaDesde > fechaHasta) {
          this.$set(this.postulacion.historial_laboral[index], 'fecha_desde', null)
          this.$set(this.postulacion.historial_laboral[index], 'fecha_hasta', null)
          notificar({ message: 'La fecha de inicio no puede ser mayor a la fecha de término. Se han limpiado los campos "Fecha Desde" y "Fecha Hasta".', severity: 'error' })
          return
        }

        this.postulacion.historial_laboral.forEach((t, i) => {
          if (i === index) return

          let tFechaDesde = null
          let tFechaHasta = null

          if (t.fecha_desde) {
            const [tdyear, tdmonth, tdday] = t.fecha_desde.split('T')[0].split('-').map(Number)
            tFechaDesde = new Date(tdyear, tdmonth - 1, tdday)
          }
          if (t.fecha_hasta) {
            const [thyear, thmonth, thday] = t.fecha_hasta.split('T')[0].split('-').map(Number)
            tFechaHasta = new Date(thyear, thmonth - 1, thday)
          }

          if (t.fecha_desde && trabajo.fecha_desde && fechaDesde.getTime() === tFechaDesde.getTime()) {
            this.$set(this.postulacion.historial_laboral[index], 'fecha_desde', null)
            notificar({ message: 'Las fechas de inicio del historial laboral no pueden ser iguales. Se han limpiado los campos "Fecha Desde"', severity: 'error' })
            return
          }

          if (t.fecha_hasta && trabajo.fecha_hasta && fechaHasta.getTime() === tFechaHasta.getTime()) {
            this.$set(this.postulacion.historial_laboral[index], 'fecha_hasta', null)
            notificar({ message: 'Las fechas de término del historial laboral no pueden ser iguales. Se han limpiado los campos "Fecha Hasta"', severity: 'error' })
            return
          }

          if (t.fecha_desde && t.fecha_hasta && trabajo.fecha_desde && (fechaDesde >= tFechaDesde && fechaDesde <= tFechaHasta)) {
            this.$set(this.postulacion.historial_laboral[index], 'fecha_desde', null)
            notificar({ message: 'Las fechas del historial laboral no pueden estar en el rango de otro. Se han limpiado los campos "Fecha Desde" del registro actual.', severity: 'error' })
            return
          }

          if (t.fecha_desde && t.fecha_hasta && trabajo.fecha_hasta && (fechaHasta >= tFechaDesde && fechaHasta <= tFechaHasta)) {
            this.$set(this.postulacion.historial_laboral[index], 'fecha_hasta', null)
            notificar({ message: 'Las fechas del historial laboral no pueden estar en el rango de otro. Se han limpiado los campos "Fecha Hasta" del registro actual.', severity: 'error' })
            return
          }

          if (t.fecha_desde && t.fecha_hasta && trabajo.fecha_desde && trabajo.fecha_hasta && (fechaDesde <= tFechaDesde && fechaHasta >= tFechaHasta)) {
            this.$set(this.postulacion.historial_laboral[index], 'fecha_hasta', null)
            this.$set(this.postulacion.historial_laboral[index], 'fecha_desde', null)
            notificar({ message: 'Las fechas del historial laboral no pueden contener el rango de otro. Se han limpiado los campos "Fecha Desde" y "Fecha Hasta" del registro actual.', severity: 'error' })
          }
        })

        if (trabajo.fecha_desde && trabajo.fecha_hasta) {
          this.actualizarTiempoTrabajado(index)
        }
      })
    },
    actualizarTiempoTrabajado(index) {
      const trabajo = this.postulacion.historial_laboral[index]
      if (trabajo.fecha_desde && trabajo.fecha_hasta) {
        const resultado = this.calcularAniosTrabajados(trabajo.fecha_desde, trabajo.fecha_hasta)
        this.$set(this.postulacion.historial_laboral[index], 'trabajado', resultado)
      }
    },
    aniosTrabajados(trabajado) {
      if (trabajado.total_dias === 0) {
        return ''
      }

      const anios = trabajado.anios
      const meses = trabajado.meses
      const dias = trabajado.dias

      const partes = []
      if (anios > 0) {
        partes.push(`${anios} año${anios !== 1 ? 's' : ''}`)
      }
      if (meses > 0) {
        partes.push(`${meses} mes${meses !== 1 ? 'es' : ''}`)
      }

      const textoAniosMeses = partes.length > 0 ? partes.join(' y ') : '0 meses'

      return `- Trabajado (${textoAniosMeses} - ${dias} días)`
    },
    montoUfBonoAdicional() {
      this.postulacion.anios_servicio_bonificacion_adicional = this.totalTrabajadoHastaPeriodoPostulacion.anios

      if (this.postulacion.anios_servicio_bonificacion_adicional < 30 && this.postulacion.causa_cese === 'pension_invalidez') {
        this.postulacion.cumple_requisitos = false
      } else if (this.postulacion.causa_cese === 'pension_invalidez' && this.postulacion.anios_servicio_bonificacion_adicional >= 30) {
        this.postulacion.cumple_requisitos = true
      }

      if (!this.postulacion.anios_servicio_bonificacion_adicional || (this.postulacion.anios_servicio_bonificacion_adicional < 10 && (this.postulacion.causa_cese === 'renuncia_voluntaria' || this.postulacion.causa_cese === 'articulo_161')) || (this.postulacion.anios_servicio_bonificacion_adicional < 30 && this.postulacion.causa_cese === 'pension_invalidez')) {
        this.postulacion.monto_uf_bonificacion_adicional = 0
        this.postulacion.solicita_bonificacion_adicional = false
        return
      }
      const elemento = this.tablaBonoAdicional.find(b => this.postulacion.anios_servicio_bonificacion_adicional >= b.anio_min && this.postulacion.anios_servicio_bonificacion_adicional <= b.anio_max)

      if (this.postulacion.sector === 'cementerio') {
        const porcentaje = this.porcentajeJornadaCementerio(this.postulacion.jornada_trabajo)/100
        this.postulacion.monto_uf_bonificacion_adicional = Math.round(elemento.monto_uf * porcentaje)
        return
      }
      this.postulacion.monto_uf_bonificacion_adicional = elemento.monto_uf
    },
    montoUfBonoTrabajo() {
      if (!this.postulacion.anios_trabajo_pesado || this.postulacion.anios_trabajo_pesado < 1) {
        this.postulacion.monto_uf_trabajo_pesado = 0
        return
      }
      const anios = this.postulacion.anios_trabajo_pesado <= 10 ? this.postulacion.anios_trabajo_pesado : 10
      const elemento = this.tablaBonoPesado.find(b => anios === b.anio)

      this.postulacion.monto_uf_trabajo_pesado = elemento.monto_uf
    },
    montoUfBonoAntiguedad() {
      if (!this.postulacion.solicita_bono_antiguedad) {
        this.postulacion.monto_uf_anios_antiguedad = 0
        return
      }
      const aniosTrabajados = this.totalTrabajado.total.anios
      this.postulacion.anios_antiguedad = aniosTrabajados


      if (!aniosTrabajados || aniosTrabajados < 35) {
        this.postulacion.monto_uf_anios_antiguedad = 0
        this.postulacion.solicita_bono_antiguedad = false
        return
      }

      const anios = aniosTrabajados <= 46 ? aniosTrabajados : 46
      const elemento = this.tablaBonoAntiguedad.find(b => anios === b.anio)

      this.postulacion.monto_uf_anios_antiguedad = elemento.monto_uf
    },
    montoBonoRetiro() {
      if (!this.postulacion.solicita_bonificacion_complementaria_retiro) {
        this.postulacion.monto_retiro_complementario = 0
      }

      if (!this.postulacion.solicita_bonificacion_retiro) {
        this.postulacion.monto_retiro_voluntario = 0
        this.postulacion.monto_retiro_complementario = 0
        return
      }

      const aniosTrabajados = this.totalTrabajado.total.anios
      if (aniosTrabajados < 1) {
        this.postulacion.solicita_bonificacion_retiro = false
        this.postulacion.solicita_bonificacion_complementaria_retiro = false
        this.postulacion.monto_retiro_voluntario = 0
        this.postulacion.monto_retiro_complementario = 0
        return
      }

      if (aniosTrabajados <= 6) {
        this.postulacion.monto_retiro_voluntario = aniosTrabajados * (this.postulacion.remuneracion_bruta || 0)
        this.postulacion.monto_retiro_complementario = 0
        this.postulacion.solicita_bonificacion_complementaria_retiro = false
      } else if (aniosTrabajados <= 11) {
        this.postulacion.monto_retiro_voluntario = 6 * (this.postulacion.remuneracion_bruta || 0)
        if (this.postulacion.solicita_bonificacion_complementaria_retiro) this.postulacion.monto_retiro_complementario = (aniosTrabajados-6) * (this.postulacion.remuneracion_bruta || 0)
      } else if (aniosTrabajados > 11) {
        this.postulacion.monto_retiro_voluntario = 6 * (this.postulacion.remuneracion_bruta || 0)
        if (this.postulacion.solicita_bonificacion_complementaria_retiro) this.postulacion.monto_retiro_complementario = 5 * (this.postulacion.remuneracion_bruta || 0)
      }
    },
    setCalidadJuridicaHistorial(index, valor) {
      this.$set(this.postulacion.historial_laboral[index], 'calidad_juridica', valor)
    },
    descargarArchivo(tipoArchivo) {
      const url = `/tramites/incentivos-retiro/postulaciones/${this.$route.params.postulacionId}/archivo/${tipoArchivo}`
      this.$axios
        .get(url, { responseType: 'blob' })
        .then((res) => {
          const blob = new Blob([res.data], { type: res.headers['content-type'] })
          const link = document.createElement('a')
          link.href = URL.createObjectURL(blob)
          link.download = url.split('/').pop()
          link.click()
          this.$emit('notificar', 'Descarga exitosa.')
        })
        .catch((error) => {
          console.log(error)
          this.$emit('notificar:error', 'El archivo seleccionado no se encuentra disponible.')
        })
    }
  },
  computed: {
    remuneracionBrutaFormateada: {
      get() {
        if (this.postulacion.remuneracion_bruta == null || this.postulacion.remuneracion_bruta === '') return ''
        return this.postulacion.remuneracion_bruta
          .toString()
          .replace(/\B(?=(\d{3})+(?!\d))/g, '.')
      },
      set(val) {
        // Elimina puntos y otros caracteres no numéricos
        const num = parseInt(val.replace(/\./g, '').replace(/[^0-9]/g, ''), 10)
        this.postulacion.remuneracion_bruta = isNaN(num) ? null : num
      }
    },
    jornadaTrabajoFormateada: {
      get() {
        if (this.postulacion.jornada_trabajo == null || this.postulacion.jornada_trabajo === '') return ''
        // Eliminar .00 si existe
        const valor = this.postulacion.jornada_trabajo.toString().replace(/\.00$/, '')
        return valor.replace('.', ',')
      },
      set(val) {
        const num = parseFloat(val.replace(',', '.').replace(/[^0-9.,]/g, ''))
        this.postulacion.jornada_trabajo = isNaN(num) ? null : num
      }
    },
    totalTrabajado() {
      const postulacionAgrupada = Object.groupBy(this.postulacion.historial_laboral, ({ municipalidad_id: municipalidadId }) => municipalidadId)

      const existePeriodoConMasDe5Anios = this.postulacion.historial_laboral.some(
        trabajo => trabajo.trabajado && trabajo.trabajado.anios >= 5
      )

      const resumenPeriodos = {}
      Object.entries(postulacionAgrupada).forEach(([municipalidadId, trabajos]) => {
        if (!resumenPeriodos[municipalidadId]) {
          resumenPeriodos[municipalidadId] = {anios: 0, meses: 0, dias: 0}
        }

        let trabajosFiltrados = trabajos

        if (!existePeriodoConMasDe5Anios) {
          trabajosFiltrados = trabajos.filter(trabajo =>
            trabajo.trabajado && trabajo.trabajado.anios >= 1
          )
        }

        trabajosFiltrados.forEach(trabajo => {
          let totalAnios = resumenPeriodos[municipalidadId].anios
          let totalMeses = resumenPeriodos[municipalidadId].meses
          let totalDias = resumenPeriodos[municipalidadId].dias

          if (trabajo.trabajado) {
            totalAnios += trabajo.trabajado.anios || 0
            totalMeses += trabajo.trabajado.meses || 0
            totalDias += trabajo.trabajado.dias || 0
          }

          if (totalDias >= 30) {
            totalMeses += Math.floor(totalDias / 30)
            totalDias = totalDias % 30
          }

          if (totalMeses >= 12) {
            totalAnios += Math.floor(totalMeses / 12)
            totalMeses = totalMeses % 12
          }

          resumenPeriodos[municipalidadId].anios = totalAnios
          resumenPeriodos[municipalidadId].meses = totalMeses
          resumenPeriodos[municipalidadId].dias = totalDias
        })

        const municipalidad = this.municipalidades.find(m => m.id === +municipalidadId)
        const nombreMunicipalidad = municipalidad ? municipalidad.nombre : 'Municipalidad no encontrada'
        resumenPeriodos[municipalidadId].municipalidadId = municipalidadId

        resumenPeriodos[municipalidadId].nombre = nombreMunicipalidad
      })

      let totalAniosResumen = 0
      let totalMesesResumen = 0
      let totalDiasResumen = 0

      Object.values(resumenPeriodos).forEach(periodo => {
        totalAniosResumen += periodo.anios
        totalMesesResumen += periodo.meses
        totalDiasResumen += periodo.dias
      })

      // Se aproxima que 30 dias corresponden a un mes
      if (totalDiasResumen >= 30) {
        totalMesesResumen += Math.floor(totalDiasResumen / 30)
        totalDiasResumen = totalDiasResumen % 30
      }

      if (totalMesesResumen >= 12) {
        totalAniosResumen += Math.floor(totalMesesResumen / 12)
        totalMesesResumen = totalMesesResumen % 12
      }

      // const partes = []
      // if (totalAniosResumen > 0) {
      //   partes.push(`${totalAniosResumen} año${totalAniosResumen !== 1 ? 's' : ''}`)
      // }
      // if (totalMesesResumen > 0) {
      //   partes.push(`${totalMesesResumen} mes${totalMesesResumen !== 1 ? 'es' : ''}`)
      // }
      // if (totalDiasResumen > 0) {
      //   partes.push(`${totalDiasResumen} día${totalDiasResumen !== 1 ? 's' : ''}`)
      // }

      // const textoTiempo = partes.length > 0 ? partes.join(', ') : '0 días'

      // return `Total: ${textoTiempo}`

      return {
        periodos: Object.values(resumenPeriodos),
        total: {
          anios: totalAniosResumen,
          meses: totalMesesResumen,
          dias: totalDiasResumen
        }
      }
    },
    totalTrabajadoHastaPeriodoPostulacion(){
      const fecha2Enero = new Date(new Date().getFullYear(), 0, 2) // 2 de enero del año actual

      let totalAnios = 0
      let totalMeses = 0
      let totalDias = 0

      this.postulacion.historial_laboral.forEach(trabajo => {
        if (!trabajo.fecha_hasta) return

        const fechaHasta = new Date(trabajo.fecha_hasta)

        if (fechaHasta > fecha2Enero) {
          const fechaDesde = new Date(trabajo.fecha_desde)

          // Si fecha_desde también es posterior al 2 de enero, contar todo el periodo
          if (fechaDesde > fecha2Enero) {
            if (trabajo.trabajado) {
              totalAnios += trabajo.trabajado.anios || 0
              totalMeses += trabajo.trabajado.meses || 0
              totalDias += trabajo.trabajado.dias || 0
            }
          } else {
            // Si fecha_desde es anterior al 2 de enero, contar solo desde el 2 de enero
            const tiempoExcedente = this.calcularAniosTrabajados(fecha2Enero.toISOString().split('T')[0], trabajo.fecha_hasta)
            totalAnios += tiempoExcedente.anios || 0
            totalMeses += tiempoExcedente.meses || 0
            totalDias += tiempoExcedente.dias || 0
          }
        }
      })

      if (totalDias >= 30) {
        totalMeses += Math.floor(totalDias / 30)
        totalDias = totalDias % 30
      }

      if (totalMeses >= 12) {
        totalAnios += Math.floor(totalMeses / 12)
        totalMeses = totalMeses % 12
      }

      const totalTrabajadoAnios = this.totalTrabajado.total.anios
      const totalTrabajadoMeses = this.totalTrabajado.total.meses
      const totalTrabajadoDias = this.totalTrabajado.total.dias

      let aniosEfectivos = totalTrabajadoAnios - totalAnios
      let mesesEfectivos = totalTrabajadoMeses - totalMeses
      let diasEfectivos = totalTrabajadoDias - totalDias

      if (diasEfectivos < 0) {
        mesesEfectivos--
        diasEfectivos += 30
      }

      if (mesesEfectivos < 0) {
        aniosEfectivos--
        mesesEfectivos += 12
      }

      // console.log('Años efectivos:', {anios: aniosEfectivos, meses: mesesEfectivos, dias: diasEfectivos})

      return {anios: aniosEfectivos, meses: mesesEfectivos, dias: diasEfectivos}
    },
    jornadaMaxima() {
      if (new Date() >= new Date(2028, 3, 1)) {
        return 40
      } else if (new Date() >= new Date(2026, 3, 1)) {
        return 42
      }
      return 44
    },
  }
}
</script>

<style scoped>
.v-list-item__title.text-h6 {
  font-weight: 600;
}
</style>
